window.PXUTheme = window.PXUTheme || {};
window.PXUTheme.contentCreator = window.PXUTheme.contentCreator || {};

(() => {
  'use strict';
  // Loaded by the theme's frame page runtime and, while CPB is installed, maybe by CPB's section too: run once.
  if (window.__gpFramePageScript) return;
  window.__gpFramePageScript = true;

  function toggleGrowplayAccordion(target) {
    const panel = target.parentElement?.nextElementSibling;
    if (!panel || panel.tagName !== 'DD') return false;

    const isExpanded = target.getAttribute('aria-expanded') === 'true';
    const nextExpanded = !isExpanded;

    target.setAttribute('aria-expanded', String(nextExpanded));
    panel.setAttribute('aria-hidden', String(!nextExpanded));
    panel.style.display = nextExpanded ? 'block' : 'none';

    return true;
  }

  function syncGrowplayAccordions(root) {
    const scope = root && root.querySelectorAll ? root : document;
    const accordions = scope.querySelectorAll('.accordion, [data-cc-accordion]');
    if (!accordions.length) return false;

    accordions.forEach((accordion) => {
      accordion.querySelectorAll(':scope > dt > a').forEach((trigger) => {
        if (!trigger.hasAttribute('aria-expanded')) trigger.setAttribute('aria-expanded', 'false');
      });

      accordion.querySelectorAll(':scope > dd').forEach((panel) => {
        if (!panel.hasAttribute('aria-hidden')) panel.setAttribute('aria-hidden', 'true');
        panel.style.display = panel.getAttribute('aria-hidden') === 'false' ? 'block' : 'none';
      });
    });

    return true;
  }

  function bindGrowplayAccordionFallback() {
    if (document.documentElement.dataset.gpAccordionFallbackBound === 'true') {
      syncGrowplayAccordions();
      return true;
    }

    const handleAccordionActivation = (event) => {
      const target = event.target.closest?.('.accordion > dt > a, [data-cc-accordion] > dt > a');
      if (!target) return;
      if (event.type === 'keydown' && event.key !== 'Enter' && event.key !== ' ') return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      toggleGrowplayAccordion(target);
    };

    document.addEventListener('click', handleAccordionActivation, true);
    document.addEventListener('keydown', handleAccordionActivation, true);
    document.documentElement.dataset.gpAccordionFallbackBound = 'true';
    syncGrowplayAccordions();
    return true;
  }

  const frameConfig = window.GrowplayFramePdp || {};
  const isFrameStockOut = () => frameConfig.is_stock_out === true || frameConfig.is_stock_out === 'true';
  const accessoryIndexNode = document.getElementById('growplay-frame-accessory-index');
  let accessoryIndex = [];

  if (accessoryIndexNode) {
    try {
      accessoryIndex = JSON.parse(accessoryIndexNode.textContent || '[]');
    } catch (_) {
      accessoryIndex = [];
    }
  }
  const accessoryRecordFetches = new Map();

  function normalizeFrameDisplayTitle(value) {
    return String(value || '').trim().replace(/\bXL PRO\b/g, 'XL Pro');
  }

  const CONFIG = {
    DEBUG: false,
    RETRY_DELAYS_MS: [0, 80, 200, 450, 900],
    OOS_RETRY_DELAYS_MS: [150, 500, 1200],
    KLAVIYO_REVIEWS_RECOVERY_DELAY_MS: 4000
  };

  const state = {
    builder: null,
    panelsContainer: null,
    builderShell: null,
    builderReturnFocus: null,
    productWrapper: null,
    customPricesContainer: null,
    priceObserver: null,
    heroPriceObserver: null,
    stickyPriceObserver: null,
    rafId: null,
    heroPriceRafId: null,
    timeouts: new Set(),
    mountedId: null,
    lastSelectedAddonKey: '',
    lastSelectedAddonNode: null,
    selectedAddonLabels: [],
    stickyHandlersBound: false,
    stickyVisibilityHandler: null,
    stickyResizeHandler: null,
    desktopStickyHandlersBound: false,
    desktopStickyScrollHandler: null,
    desktopStickyResizeHandler: null,
    desktopStickyUpdate: null,
    desktopStickyRafId: null,
    desktopMediaStickyHandlersBound: false,
    desktopMediaStickyScrollHandler: null,
    desktopMediaStickyResizeHandler: null,
    desktopMediaStickyUpdate: null,
    desktopMediaStickyRafId: null,
    addonEventsBound: false,
    addonClickHandler: null,
    addonTileActivationHandler: null,
    tooltipScrubberBound: false,
    tooltipPointerHandler: null,
    tooltipFocusHandler: null,
    addonTileRefreshFrame: null,
    addonTileRefreshTimeoutId: null,
    builderCartButtonsBound: false,
    builderDiscountSyncTimeouts: [],
    builderDiscountObserver: null,
    selectedAdventureVariantId: '',
    chooseAdventurePromptReturnFocus: null,
    klaviyoReviewsReloadTimeoutId: null,
    addonClassObserver: null,
    chooseAdventureObserver: null,
    tooltipObserver: null,
    afterpayShadowObserver: null,
    heroStarObserver: null,
    discountScriptPromise: null,
    videoPromoBannerPromise: null
  };

  function isCompactFramePdp() {
    return frameConfig.frame_id === 'compact';
  }

  const utils = {
    qs: (selector, parent = document) => parent.querySelector(selector),
    qsa: (selector, parent = document) => parent.querySelectorAll(selector),

    log(...args) {
      if (CONFIG.DEBUG) console.log('CPB:', ...args);
    },

    setTrackedTimeout(fn, delay) {
      const id = setTimeout(() => {
        state.timeouts.delete(id);
        fn();
      }, delay);
      state.timeouts.add(id);
      return id;
    },

    clearAllTimeouts() {
      state.timeouts.forEach(clearTimeout);
      state.timeouts.clear();
    },

    retry(fn, delays = CONFIG.RETRY_DELAYS_MS) {
      delays.forEach((delay) => {
        utils.setTrackedTimeout(() => {
          try {
            fn();
          } catch (_) {}
        }, delay);
      });
    },

    isVisible(el) {
      return !!(el && el.offsetParent !== null);
    },

    newMountId() {
      return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    },

    normalizeText(value) {
      return (value || '')
        .toString()
        .toLowerCase()
        .replace(/&/g, 'and')
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();
    },

    escapeHtml(value) {
      return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    },

    toTitleCase(value) {
      return String(value || '')
        .toLowerCase()
        .replace(/\b([a-z0-9])/g, (match) => match.toUpperCase());
    }
  };

  function isFramePdp() {
    return !!frameConfig.is_frame_pdp;
  }

  function getBackorderStatusText() {
    return String(frameConfig.backorder_status_text || '').trim();
  }

  function getBackorderStatusDetails() {
    return String(frameConfig.backorder_status_details || '').trim();
  }

  function createBackorderStatusPill(extraClass = '') {
    const text = getBackorderStatusText();
    if (!text) return null;
    const details = getBackorderStatusDetails();

    const pill = document.createElement('div');
    pill.className = `purchase-details__status-pill purchase-details__status-pill--backorder ${extraClass}`.trim();
    pill.setAttribute('role', 'status');
    pill.innerHTML = `
      <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
        <path d="M6 2a1 1 0 0 1 1 1v1h6V3a1 1 0 1 1 2 0v1h1.5A2.5 2.5 0 0 1 19 6.5v9A2.5 2.5 0 0 1 16.5 18h-13A2.5 2.5 0 0 1 1 15.5v-9A2.5 2.5 0 0 1 3.5 4H5V3a1 1 0 0 1 1-1Zm10.5 6h-13v7.5a.5.5 0 0 0 .5.5h12a.5.5 0 0 0 .5-.5V8Z" fill="currentColor"></path>
      </svg>
      <span data-backorder-status-label>${utils.escapeHtml(text)}</span>
      ${details ? `
        <details class="gp-backorder-info">
          <summary aria-label="Order availability details">i</summary>
          <span class="gp-backorder-info__popup">${utils.escapeHtml(details)}</span>
        </details>
      ` : ''}
    `;

    return pill;
  }

  function getConfiguredFreeAddonItems() {
    const items = Array.isArray(frameConfig.free_addons) ? frameConfig.free_addons : [];
    return items
      .map((item) => ({
        title: String(item?.title || '').replace(/^free\s+/i, '').trim(),
        handle: String(item?.handle || '').trim(),
        image: String(item?.image || '').trim(),
        compareAt: Number(item?.compare_at_cents || item?.compareAt || 0),
        variantId: Number(item?.variant_id || item?.variantId || 0),
        available: item?.available !== false
      }))
      .filter((item) => item.title || item.handle || item.image || item.compareAt > 0 || item.variantId > 0);
  }


  function hasConfiguredFreeAddonOffer() {
    return Number(frameConfig.free_addons_count || 0) > 0 || getConfiguredFreeAddonItems().length > 0;
  }

  function includedGiftTitle(title) {
    return utils.escapeHtml(String(title || 'Add-on').replace(/^free\s+/i, '').toLowerCase().replace(/(^|\s)\S/g, (letter) => letter.toUpperCase())) + ' included';
  }

  function buildFreeAddonOfferMarkup(data) {
    if (!data) return '';

    const items = Array.isArray(data.items) ? data.items.filter(Boolean) : [];
    if (items.length > 1) {
      const compareAtTotal = Number(data.compareAt || items.reduce((total, item) => total + Number(item.compareAt || 0), 0));
      const cards = items.slice(0, 3).map((item) => {
        const title = String(item.title || '').trim();
        const image = String(item.image || '').trim();
        const compareAt = Number(item.compareAt || 0);

        return `
          <div class="gp-frame-free-addon-offer__card">
            ${image ? `<img class="gp-frame-free-addon-offer__card-image" src="${image}" alt="${title || 'Free add-on'}">` : ''}
            ${title ? `<div class="gp-frame-free-addon-offer__card-title">${title}</div>` : ''}
            ${compareAt > 0 ? `
              <div class="gp-frame-free-addon-offer__card-pricing">
                <span class="gp-frame-free-addon-offer__card-price">${formatMoneyCents(compareAt)}</span>
                <span class="gp-frame-free-addon-offer__card-free">Free</span>
              </div>
            ` : ''}
          </div>
        `;
      }).join('');

      return `
        <div class="gp-frame-free-addon-offer__header">
          <h2 class="gp-frame-free-addon-offer__heading">Includes ${items.length} free add-ons</h2>
          ${compareAtTotal > 0 ? `<span class="gp-frame-free-addon-offer__value-pill">Total value ${formatMoneyCents(compareAtTotal)}</span>` : ''}
        </div>
        <div class="gp-frame-free-addon-offer__grid">
          ${cards}
        </div>
        <p class="gp-frame-free-addon-offer__note">Added automatically at checkout</p>
      `;
    }

    const title = String(data.title || '').trim();
    const compareAt = Number(data.compareAt || 0);
    const image = String(data.image || '').trim();

    if (!title && compareAt <= 0 && !image) return '';

    return `
      <div class="gp-frame-free-addon-offer__media">
        ${image ? `<img src="${image}" alt="${title || 'Free add-on'}">` : ''}
      </div>
      <div class="gp-frame-free-addon-offer__content">
        <h3 class="gp-frame-free-addon-offer__title">${includedGiftTitle(title)}</h3>
        <div class="gp-frame-free-addon-offer__compare">${compareAt > 0 ? `<s class="gp-frame-free-addon-offer__was">${formatWholeAdventureMoneyCents(compareAt)}</s>` : ''}<strong class="gp-frame-free-addon-offer__free">Free</strong><span class="gp-frame-free-addon-offer__caption">${utils.escapeHtml(window.PXUTheme.translation.gift_added_with_frame)}</span></div>
      </div>
    `;
  }

  function setFreeAddonOfferCountClass(teaser, count) {
    if (!teaser) return;
    teaser.classList.remove(
      'gp-frame-free-addon-offer--count-1',
      'gp-frame-free-addon-offer--count-2',
      'gp-frame-free-addon-offer--count-3'
    );
    if (count > 0) {
      teaser.classList.add('gp-frame-free-addon-offer--count-' + Math.min(count, 3));
    }
  }

  function getImmediateFreeAddonOfferData() {
    if (!isFramePdp()) return null;

    const configuredItems = getConfiguredFreeAddonItems();
    if (!hasConfiguredFreeAddonOffer()) return null;

    if (configuredItems.length > 1) {
      return {
        title: '',
        subtitle: String(frameConfig.free_addon_copy || '').trim() || 'Included with your frame - limited time',
        compareAt: Number(frameConfig.free_addons_total_value_cents || 0)
          || configuredItems.reduce((total, item) => total + Number(item.compareAt || 0), 0),
        image: String(configuredItems[0]?.image || '').trim(),
        variantId: Number(configuredItems[0]?.variantId || 0),
        items: configuredItems
      };
    }

    const configuredItem = configuredItems[0] || null;
    const configuredTitle = String(frameConfig.free_addon_label || '').replace(/^free\s+/i, '').trim();
    const configuredHandle = String(frameConfig.free_addon_handle || '').trim()
      || (utils.normalizeText(configuredTitle) ? configuredTitle.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') : '');
    const configuredCompareAt = Number(frameConfig.free_addon_compare_at_cents || 0)
      || (Number(frameConfig.free_addon_value || 0) > 0 ? Number(frameConfig.free_addon_value || 0) * 100 : 0);
    const configuredImage = String(frameConfig.free_addon_image || '').trim();
    const configuredVariantId = Number(frameConfig.free_addon_variant_id || 0);

    const titleSeed = configuredItem?.title || configuredTitle;
    const handleSeed = configuredItem?.handle || configuredHandle;
    const record = findAccessoryRecord(titleSeed)
      || accessoryIndex.find((item) => item && item.handle === handleSeed)
      || null;

    const title = titleSeed || record?.title || '';
    const compareAt = Number(record?.compare_at_price || configuredItem?.compareAt || configuredCompareAt || record?.price || 0);

    if (!title && compareAt <= 0 && !configuredImage && !configuredItem?.image) return null;

    return {
      title,
      subtitle: String(frameConfig.free_addon_copy || '').trim() || 'Included with your frame - limited time',
      compareAt,
      image: configuredItem?.image || configuredImage || String(record?.image || '').trim(),
      variantId: configuredItem?.variantId || configuredVariantId || Number(record?.variant_id || 0),
      items: configuredItem ? [configuredItem] : []
    };
  }

  function restoreZipWidget() {
    const widget = document.getElementById('shopify-zip-prod-widget');
    const originalHost = document.querySelector('[data-zip-original-host]');
    const cpbMount = document.querySelector('.cpb-zip-widget-mount');

    if (cpbMount) cpbMount.remove();
    if (widget && originalHost && widget.parentElement !== originalHost) {
      originalHost.appendChild(widget);
    }
  }

  function restoreAfterpayPlacement() {
    const unified = document.querySelector('gp-bnpl[data-context="builder"]');
    const unifiedHost = document.querySelector('[data-gp-bnpl-builder-host]');
    if (unified && unifiedHost && unified.parentElement !== unifiedHost) unifiedHost.append(unified);

    const paymentRow = document.querySelector('[data-cpb-payment-messaging]');
    const paymentHost = document.querySelector('[data-cpb-payment-original-host]');
    if (paymentRow && paymentHost && paymentRow.parentElement !== paymentHost) {
      paymentHost.appendChild(paymentRow);
    }
    const placement = document.querySelector('[data-cpb-afterpay-placement]');
    const originalHost = document.querySelector('[data-afterpay-original-host]');
    const afterpayMount = document.querySelector('.cpb-afterpay-mount');

    if (afterpayMount) afterpayMount.remove();
    if (placement && originalHost && placement.parentElement !== originalHost) {
      originalHost.appendChild(placement);
      placement.style.display = '';
    }
  }

  function syncHeroPaymentWidgets() {
    if (isBuilderOpen()) return;

    const heroPriceText = findHeroPriceText();
    if (heroPriceText) syncStandardAfterpayAmount(heroPriceText);
  }

  function loadFrameDiscountScript() {
    if (!isFramePdp()) return Promise.resolve(false);

    const scriptSrc = String(frameConfig.cpb_discount_script || '').trim();
    if (!scriptSrc) return Promise.resolve(false);

    const existing = document.querySelector('script[data-gp-cpb-discounts="true"]');
    if (existing) return Promise.resolve(true);
    if (state.discountScriptPromise) return state.discountScriptPromise;

    state.discountScriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = scriptSrc;
      script.async = true;
      script.setAttribute('data-gp-cpb-discounts', 'true');
      script.onload = () => resolve(true);
      script.onerror = () => {
        state.discountScriptPromise = null;
        reject(new Error('Failed to load CPB discount script'));
      };
      document.head.appendChild(script);
    });

    return state.discountScriptPromise;
  }

  function cleanup() {
    utils.clearAllTimeouts();

    if (state.rafId) {
      cancelAnimationFrame(state.rafId);
      state.rafId = null;
    }

    if (state.priceObserver) {
      state.priceObserver.disconnect();
      state.priceObserver = null;
    }

    if (state.builderDiscountObserver) {
      state.builderDiscountObserver.disconnect();
      state.builderDiscountObserver = null;
    }
    state.builderDiscountSyncTimeouts.forEach(clearTimeout);
    state.builderDiscountSyncTimeouts = [];

    if (state.heroPriceObserver) {
      state.heroPriceObserver.disconnect();
      state.heroPriceObserver = null;
    }

    if (state.stickyPriceObserver) {
      state.stickyPriceObserver.disconnect();
      state.stickyPriceObserver = null;
    }

    if (state.stickyHandlersBound) {
      if (state.stickyVisibilityHandler) {
        window.removeEventListener('scroll', state.stickyVisibilityHandler);
      }
      if (state.stickyResizeHandler) {
        window.removeEventListener('resize', state.stickyResizeHandler);
      }
      state.stickyHandlersBound = false;
      state.stickyVisibilityHandler = null;
      state.stickyResizeHandler = null;
    }

    if (state.desktopStickyHandlersBound) {
      if (state.desktopStickyScrollHandler) {
        window.removeEventListener('scroll', state.desktopStickyScrollHandler);
      }
      if (state.desktopStickyResizeHandler) {
        window.removeEventListener('resize', state.desktopStickyResizeHandler);
      }
      state.desktopStickyHandlersBound = false;
      state.desktopStickyScrollHandler = null;
      state.desktopStickyResizeHandler = null;
      state.desktopStickyUpdate = null;
    }

    if (state.desktopStickyRafId) {
      cancelAnimationFrame(state.desktopStickyRafId);
      state.desktopStickyRafId = null;
    }

    if (state.desktopMediaStickyHandlersBound) {
      if (state.desktopMediaStickyScrollHandler) {
        window.removeEventListener('scroll', state.desktopMediaStickyScrollHandler);
      }
      if (state.desktopMediaStickyResizeHandler) {
        window.removeEventListener('resize', state.desktopMediaStickyResizeHandler);
      }
      state.desktopMediaStickyHandlersBound = false;
      state.desktopMediaStickyScrollHandler = null;
      state.desktopMediaStickyResizeHandler = null;
      state.desktopMediaStickyUpdate = null;
    }

    if (state.desktopMediaStickyRafId) {
      cancelAnimationFrame(state.desktopMediaStickyRafId);
      state.desktopMediaStickyRafId = null;
    }

    if (state.klaviyoReviewsReloadTimeoutId) {
      clearTimeout(state.klaviyoReviewsReloadTimeoutId);
      state.klaviyoReviewsReloadTimeoutId = null;
    }

    if (state.addonClassObserver) {
      state.addonClassObserver.disconnect();
      state.addonClassObserver = null;
    }

    if (state.chooseAdventureObserver) {
      state.chooseAdventureObserver.disconnect();
      state.chooseAdventureObserver = null;
    }

    if (state.tooltipObserver) {
      state.tooltipObserver.disconnect();
      state.tooltipObserver = null;
    }

    if (state.addonTileActivationHandler) {
      document.removeEventListener('click', state.addonTileActivationHandler, true);
      document.removeEventListener('keydown', state.addonTileActivationHandler, true);
      state.addonTileActivationHandler = null;
    }

    if (state.addonClickHandler) {
      document.removeEventListener('click', state.addonClickHandler, true);
      state.addonClickHandler = null;
    }

    if (state.tooltipScrubberBound) {
      if (state.tooltipPointerHandler) {
        document.removeEventListener('pointerover', state.tooltipPointerHandler, true);
      }
      if (state.tooltipFocusHandler) {
        document.removeEventListener('focusin', state.tooltipFocusHandler, true);
      }
      state.tooltipPointerHandler = null;
      state.tooltipFocusHandler = null;
      state.tooltipScrubberBound = false;
    }

    if (state.afterpayShadowObserver) {
      state.afterpayShadowObserver.disconnect();
      state.afterpayShadowObserver = null;
    }

    if (state.heroStarObserver) {
      state.heroStarObserver.disconnect();
      state.heroStarObserver = null;
    }

    restoreZipWidget();
    restoreAfterpayPlacement();

    // CPB can emit more than one mount event; replace the previous presentation.
    utils.qsa('#product-builder .cpb-custom-prices-container, #product-builder .cpb-payment-inline').forEach(node => node.remove());
    state.builder = null;
    state.panelsContainer = null;
    state.builderShell = null;
    state.productWrapper = null;
    state.customPricesContainer = null;
    state.mountedId = null;
    state.selectedAdventureVariantId = '';
    state.lastSelectedAddonKey = '';
    state.lastSelectedAddonNode = null;
    state.selectedAddonLabels = [];
    state.builderCartButtonsBound = false;
    state.heroPriceRafId = null;
  }

  /* -----------------------------------------------------------
     Static SVG star rating — used in the builder view where we
     can't use the live Klaviyo widget.
     ----------------------------------------------------------- */
  function buildStaticStarHtml(ratingVal, ratingCount) {
    if (!ratingVal || !ratingCount) return '';
    var starColor = '#F8BE00';
    var emptyColor = '#E5E5E5';
    // Use the same star path as Klaviyo so hero and builder stars are identical
    var starPath = 'M11.1488 2.23197C10.7159 1.22183 9.28382 1.22183 8.85091 2.23197L6.95034 6.66662H2.67243C1.5588 6.66662 1.00109 8.01305 1.78854 8.8005L4.91029 11.9222L3.59487 17.1839C3.32787 18.2519 4.47191 19.1186 5.42772 18.5724L9.99984 15.9597L14.572 18.5724C15.5278 19.1186 16.6718 18.2519 16.4048 17.1839L15.0894 11.9222L18.2111 8.8005C18.9986 8.01304 18.4409 6.66662 17.3272 6.66662H13.0493L11.1488 2.23197Z';
    var starsHtml = '';
    for (var s = 1; s <= 5; s++) {
      var fill = s <= Math.floor(ratingVal) ? starColor : emptyColor;
      if (s > Math.floor(ratingVal) && s - 1 < ratingVal) {
        var pct = Math.round((ratingVal - Math.floor(ratingVal)) * 100);
        starsHtml += '<svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">' +
          '<defs><linearGradient id="gp-star-' + s + '"><stop offset="' + pct + '%" stop-color="' + starColor + '"/><stop offset="' + pct + '%" stop-color="' + emptyColor + '"/></linearGradient></defs>' +
          '<path d="' + starPath + '" fill="url(#gp-star-' + s + ')"/></svg>';
      } else {
        starsHtml += '<svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true"><path d="' + starPath + '" fill="' + fill + '"/></svg>';
      }
    }
    return starsHtml;
  }

  function getKlaviyoRatingData() {
    var klBtn = document.querySelector('.klaviyo-star-rating-widget button[aria-label]');
    if (klBtn) {
      var match = klBtn.getAttribute('aria-label').match(/([\d.]+)\s*stars?,\s*(\d+)/i);
      if (match) return { val: parseFloat(match[1]), count: parseInt(match[2], 10) };
    }

    var staticHeroRating = document.querySelector('[data-frame-hero-rating][data-rating-value][data-rating-count]');
    if (staticHeroRating) {
      var ratingValue = parseFloat(staticHeroRating.getAttribute('data-rating-value'));
      var ratingCount = parseInt(staticHeroRating.getAttribute('data-rating-count'), 10);
      var scaleMax = parseFloat(staticHeroRating.getAttribute('data-rating-scale-max')) || 5;
      if (ratingValue > 0 && ratingCount > 0 && scaleMax > 0) {
        return {
          val: (ratingValue / scaleMax) * 5,
          count: ratingCount
        };
      }
    }

    return null;
  }

  function getHeroRatingHost() {
    var host = document.querySelector(
      '.gp-frame-product__information [data-gp-klaviyo-host="true"],' +
      '.gp-frame-product__information .product-block--\\@app:has(.klaviyo-star-rating-widget),' +
      '.gp-frame-product__information [class*="product-block--@"]:has(.klaviyo-star-rating-widget),' +
      '.gp-frame-product__information .product-block--app,' +
      '.gp-frame-product__information .product-block--\\@app,' +
      '.gp-frame-product__information [class*="product-block--@"],' +
      '.gp-frame-product__information .product-block--rating'
    );

    if (!host) return null;
    host.setAttribute('data-gp-klaviyo-host', 'true');
    return host;
  }

  function syncHeroRatingHost() {
    if (document.querySelector('.gp-frame-hero-rating')) return null;

    var host = getHeroRatingHost();
    if (!host) return null;

    var hasRealWidget = !!host.querySelector('.klaviyo-star-rating-widget:not(.gp-frame-star-fallback)');
    var hasFallback = !!host.querySelector('.gp-frame-star-fallback');

    if (hasRealWidget || hasFallback) {
      host.style.display = 'block';
    } else {
      host.style.removeProperty('display');
    }

    return host;
  }

  function renderHeroStaticRating(appBlock, data) {
    if (!appBlock || !data || !data.val || !data.count) return false;

    var realWidget = appBlock.querySelector('.klaviyo-star-rating-widget:not(.gp-frame-star-fallback)');
    var staticRating = appBlock.querySelector('.gp-frame-star-fallback');
    var starsHtml = buildStaticStarHtml(data.val, data.count);
    if (!starsHtml) return false;

    if (!staticRating) {
      staticRating = document.createElement('div');
      staticRating.className = 'gp-frame-star-fallback klaviyo-star-rating-widget';
      staticRating.style.cssText = 'display:inline-flex;align-items:center;gap:4px;';
      appBlock.appendChild(staticRating);
    }

    staticRating.innerHTML =
      '<a href="#klaviyo-product-reviews-wrapper" style="display:inline-flex;align-items:center;gap:2px;text-decoration:none;cursor:pointer;">' +
        starsHtml +
        '<span style="font-size:13px;color:#4a5d6b;margin-left:4px;">' + data.count + ' reviews</span>' +
      '</a>';

    if (realWidget) {
      realWidget.style.display = 'none';
      realWidget.setAttribute('aria-hidden', 'true');
    }

    syncHeroRatingHost();
    return true;
  }

  function normalizeHeroKlaviyoStars() {
    if (!isFramePdp()) return false;
    if (document.querySelector('.gp-frame-hero-rating')) return false;
    var appBlock = getHeroRatingHost();
    if (!appBlock) return false;
    return renderHeroStaticRating(appBlock, getKlaviyoRatingData());
  }

  /**
   * Star-rating fallback: if Klaviyo hasn't rendered within 2.5s,
   * inject static stars into the hero app block so users always see ratings.
   * Klaviyo widget will replace them if/when it eventually loads.
   */
  function ensureHeroStars() {
    if (!isFramePdp()) return;
    if (document.querySelector('.gp-frame-hero-rating')) return;

    // If Klaviyo has rendered, replace its hero row with the same static gold
    // SVG stars used inside the builder so pre-builder and builder match.
    if (document.querySelector('.klaviyo-star-rating-widget button[aria-label]')) {
      normalizeHeroKlaviyoStars();
      return;
    }

    var appBlock = getHeroRatingHost();
    if (!appBlock) return;

    // If block already has our fallback, skip
    if (appBlock.querySelector('.gp-frame-star-fallback')) {
      syncHeroRatingHost();
      return;
    }

    // Try to get cached rating from the page's Klaviyo JSON or meta
    var ratingVal = 0, ratingCount = 0;
    // Check if Klaviyo embedded data exists
    var klScript = document.querySelector('script[type="application/json"][data-klaviyo-reviews]');
    if (klScript) {
      try {
        var d = JSON.parse(klScript.textContent);
        ratingVal = d.average || d.rating || 0;
        ratingCount = d.count || d.total || 0;
      } catch(e) {}
    }
    // Fallback: check meta tags
    if (!ratingVal) {
      var metaRating = document.querySelector('meta[itemprop="ratingValue"], meta[property="og:rating"]');
      var metaCount = document.querySelector('meta[itemprop="reviewCount"], meta[itemprop="ratingCount"]');
      if (metaRating) ratingVal = parseFloat(metaRating.getAttribute('content')) || 0;
      if (metaCount) ratingCount = parseInt(metaCount.getAttribute('content'), 10) || 0;
    }
    // Fallback: parse JSON-LD structured data
    if (!ratingVal) {
      var ldScripts = document.querySelectorAll('script[type="application/ld+json"]');
      ldScripts.forEach(function(s) {
        if (ratingVal) return;
        try {
          var ld = JSON.parse(s.textContent);
          if (ld.aggregateRating) {
            ratingVal = parseFloat(ld.aggregateRating.ratingValue) || 0;
            ratingCount = parseInt(ld.aggregateRating.ratingCount || ld.aggregateRating.reviewCount, 10) || 0;
          }
        } catch(e) {}
      });
    }

    if (!ratingVal || !ratingCount) return;

    renderHeroStaticRating(appBlock, { val: ratingVal, count: ratingCount });

    // Watch for real Klaviyo to render and remove our fallback
    if (state.heroStarObserver) {
      state.heroStarObserver.disconnect();
    }

    state.heroStarObserver = new MutationObserver(function() {
      if (appBlock.querySelector('.klaviyo-star-rating-widget:not(.gp-frame-star-fallback) button[aria-label]')) {
        normalizeHeroKlaviyoStars();
        state.heroStarObserver.disconnect();
        state.heroStarObserver = null;
      }
    });
    state.heroStarObserver.observe(appBlock, { childList: true, subtree: true });
  }

  /**
   * Fix Afterpay square-placement shadow DOM font size
   * to match Zip at 12.5px.
   */
  function fixAfterpayShadowDom() {
    var placements = document.querySelectorAll('.gp-frame-primary-bnpl square-placement, #product-builder .cpb-afterpay-mount square-placement');
    var applied = false;
    placements.forEach(function(sq) {
      if (!sq.shadowRoot) return;
      var style = sq.shadowRoot.querySelector('style[data-gp-fix]');
      if (style) return; // already fixed
      var s = document.createElement('style');
      s.setAttribute('data-gp-fix', '1');
      s.textContent = ':host { font-size: 12.5px !important; line-height: 1.3 !important; color: #475b63 !important; } *, *::before, *::after { font-size: inherit !important; line-height: inherit !important; color: inherit !important; }';
      sq.shadowRoot.appendChild(s);
      applied = true;
    });
    return applied;
  }

  function scheduleAfterpayShadowFix(delays) {
    utils.retry(fixAfterpayShadowDom, delays || [0, 150, 450, 1000, 2000]);
  }

  function observeAfterpayPlacements() {
    if (isFramePdp() && !isBuilderOpen()) return;
    if (state.afterpayShadowObserver || !document.body) return;

    state.afterpayShadowObserver = new MutationObserver(function() {
      if (isBuilderOpen()) {
        fixAfterpayShadowDom();
        return;
      }
    });
    state.afterpayShadowObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  function initGlobals() {
    state.builder = utils.qs('#product-builder');
    if (!state.builder) return false;

    state.panelsContainer = utils.qs('.cpb-panels-container', state.builder);
    state.builderShell = state.builder.closest('.shopify-section');
    state.productWrapper = utils.qs('.gp-frame-pdp.cpb-basic-product, .cpb-basic-product.gp-frame-pdp');
    return !!state.panelsContainer;
  }

  const PRICE_REGEX = /(\d[\d,]*)/;
  const PRICE_DECIMAL_REGEX = /(\d[\d,]*(?:[.,]\d{1,2})?)/;

  function getCurrencyPrefix(raw) {
    const source = String(raw || '');
    const symbolMatch = source.match(/(?:£|€|\$)/);
    if (symbolMatch) return symbolMatch[0];

    const moneyFormat = window.theme?.moneyFormat || window.Shopify?.moneyFormat || '';
    const formatSymbolMatch = String(moneyFormat).match(/(?:£|€|\$)/);
    return formatSymbolMatch ? formatSymbolMatch[0] : '$';
  }

  function formatPrice(text) {
    const raw = (text || '').trim();
    const match = raw.match(PRICE_REGEX);
    const currencyPrefix = getCurrencyPrefix(raw);
    if (!match) return currencyPrefix + '0';

    const wholeNumber = match[1].replace(/,/g, '');
    return currencyPrefix + wholeNumber.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function parseAmount(text) {
    const raw = (text || '').trim();
    const match = raw.match(PRICE_DECIMAL_REGEX);
    if (!match) return null;

    const token = match[1];
    const hasDot = token.includes('.');
    const hasComma = token.includes(',');
    let normalized = token;

    if (hasDot && hasComma) {
      const decimalSeparator = token.lastIndexOf('.') > token.lastIndexOf(',') ? '.' : ',';
      normalized = decimalSeparator === '.' ? token.replace(/,/g, '') : token.replace(/\./g, '').replace(',', '.');
    } else if (hasComma && /,\d{1,2}$/.test(token)) {
      normalized = token.replace(/\./g, '').replace(',', '.');
    } else {
      normalized = token.replace(/,/g, '');
    }

    const amount = Number(normalized);
    return Number.isFinite(amount) ? amount : null;
  }

  function syncAfterpayAmount(priceText) {
    const unified = document.querySelector('gp-bnpl[data-context="builder"]');
    if (unified) {
      const amount = parseAmount(priceText);
      if (amount !== null) unified.amount = Math.round(amount * 100);
      return;
    }

    const placement = document.querySelector('[data-cpb-afterpay-placement]');
    if (!placement) return;

    const amount = parseAmount(priceText);
    if (amount === null) return;

    const value = amount.toFixed(2);
    if (placement.getAttribute('data-amount') !== value) placement.setAttribute('data-amount', value);
    const zip = document.querySelector('[data-cpb-payment-messaging] [data-zm-price]');
    if (zip && zip.getAttribute('data-zm-price') !== value) zip.setAttribute('data-zm-price', value);
    const paypalMessage = document.querySelector('[data-cpb-payment-messaging] [data-pp-message]');
    if (paypalMessage && paypalMessage.getAttribute('data-pp-amount') !== value) paypalMessage.setAttribute('data-pp-amount', value);
  }

  function syncStandardAfterpayAmount(priceText) {
    const amount = parseAmount(priceText);
    if (amount === null) return;

    const placement = document.querySelector('[data-afterpay-product-placement]');
    if (placement) {
      const value = amount.toFixed(2);
      if (placement.getAttribute('data-amount') !== value) placement.setAttribute('data-amount', value);
    }
  }

  function findBestPriceEl() {
    const prices = Array.from(utils.qsa('.cpb-product-price', state.panelsContainer));
    if (!prices.length) return null;

    const visible = prices.slice().reverse().find(utils.isVisible);
    return visible || prices[prices.length - 1];
  }

  function findHeroPriceText() {
    const money = utils.qs('.gp-frame-product__information .modal_price .money, .product__information .modal_price .money');
    if (!money) return '';
    return money.textContent || '';
  }

  function keepBaseProductVisible() {
    setBaseProductVisibility(true);
  }

  function isBuilderOpen() {
    return document.documentElement.classList.contains('gp-frame-builder-open');
  }

  function setBaseProductVisibility(visible) {
    if (!isFramePdp()) return;

    state.productWrapper = state.productWrapper || utils.qs('.gp-frame-pdp.cpb-basic-product, .cpb-basic-product.gp-frame-pdp');
    if (!state.productWrapper) return;

    state.productWrapper.style.display = visible ? '' : 'none';
    state.productWrapper.hidden = !visible;
    state.productWrapper.setAttribute('aria-hidden', visible ? 'false' : 'true');
  }

  function refreshBuilderRefs() {
    state.builder = utils.qs('#product-builder');
    state.builderShell = state.builder ? state.builder.closest('.shopify-section') : null;

    if (!state.panelsContainer && state.builder) {
      state.panelsContainer = utils.qs('.cpb-panels-container', state.builder);
    }

    return !!(state.builder || state.builderShell);
  }

  function releaseBuilderCollapsedState() {
    if (!state.builder) return;

    state.builder.hidden = false;
    state.builder.removeAttribute('hidden');
    state.builder.style.removeProperty('display');
    // The native builder clears its own load-time styles when it mounts.
    if (state.builder.hasAttribute('data-gp-native-builder')) return;
    state.builder.style.setProperty('opacity', '1', 'important');
    state.builder.style.setProperty('pointer-events', 'auto', 'important');
    state.builder.style.setProperty('height', 'auto', 'important');
    state.builder.style.removeProperty('min-height');
    utils.qsa('.cpb-root, .cpb-product-builder, .cpb-theme-provider, .cpb-layout, .cpb-panels-container', state.builder).forEach((node) => {
      node.style.removeProperty('min-height');
    });
  }

  function normalizeBuilderDescriptionContent() {
    const root = state.builder || document;
    const description = utils.qs('.cpb-custom-product-description-container .cpb-product-description, #product-builder .cpb-product-description', root);
    if (!description) return false;

    const accordions = utils.qsa('.accordion dd, [data-cc-accordion] dd', description);
    let changed = false;

    accordions.forEach((panel) => {
      const headingText = (panel.previousElementSibling?.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
      if (headingText.includes('installation requirements') && !panel.classList.contains('gp-cpb-plain-text-panel')) {
        panel.classList.add('gp-cpb-plain-text-panel');
        changed = true;
      }

      if (panel.dataset.gpRichTextNormalized === 'true') return;
      if (panel.querySelector('ul, ol, table')) {
        panel.dataset.gpRichTextNormalized = 'true';
        return;
      }

      const unsupportedElement = Array.from(panel.children).some((child) => {
        const tag = child.tagName;
        return !['BR', 'EM', 'I', 'STRONG', 'B', 'SPAN', 'P', 'DIV'].includes(tag);
      });
      if (unsupportedElement) return;

      const lines = [];
      let current = '';

      const pushLine = () => {
        const line = current.replace(/\s+/g, ' ').trim();
        if (line) lines.push(line);
        current = '';
      };

      const walkNode = (node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          current += node.textContent || '';
          return;
        }

        if (node.nodeType !== Node.ELEMENT_NODE) return;

        if (node.tagName === 'BR') {
          pushLine();
          return;
        }

        if (['P', 'DIV'].includes(node.tagName)) {
          pushLine();
          node.childNodes.forEach(walkNode);
          pushLine();
          return;
        }

        node.childNodes.forEach(walkNode);
      };

      panel.childNodes.forEach(walkNode);

      pushLine();

      const normalizedLines = [];
      lines.forEach((line) => {
        const noteMatch = line.match(/^(.*?)(\s+\*.+)$/);
        if (noteMatch && noteMatch[1].trim()) {
          normalizedLines.push(noteMatch[1].trim());
          normalizedLines.push(noteMatch[2].trim());
          return;
        }
        normalizedLines.push(line);
      });

      const itemLines = normalizedLines.filter((line) => !/^\*/.test(line));
      const noteLines = normalizedLines.filter((line) => /^\*/.test(line));
      if (itemLines.length < 3) return;

      const list = document.createElement('ul');
      list.className = 'cpb-description-list';
      itemLines.forEach((line) => {
        const item = document.createElement('li');
        item.textContent = line;
        list.appendChild(item);
      });

      panel.textContent = '';
      panel.appendChild(list);

      noteLines.forEach((line) => {
        const note = document.createElement('p');
        note.className = 'cpb-description-note';
        const em = document.createElement('em');
        em.textContent = line;
        note.appendChild(em);
        panel.appendChild(note);
      });

      panel.dataset.gpRichTextNormalized = 'true';
      changed = true;
    });

    return changed;
  }

  function prepareBuilderNavigation() {
    const shell = state.builderShell;
    if (!shell) return;
    shell.setAttribute('role', 'region');
    shell.setAttribute('aria-label', 'Customise your setup');
    shell.setAttribute('tabindex', '-1');
  }

  function focusBuilderEntry() {
    const entry = state.builder?.querySelector('.cpb-product-title') || state.builderShell;
    if (!entry) return;
    entry.setAttribute('tabindex', '-1');
    entry.focus({preventScroll: true});
  }

  document.addEventListener('gp:builder-cancel', () => closeBuilder());

  function closeBuilder() {
    setBuilderVisibility(false);
    const restoreFocus = () => {
      const candidates = [state.builderReturnFocus, ...utils.qsa('[data-frame-open-builder], .personalize-it--js')];
      const trigger = candidates.find(node => node?.isConnected && !node.disabled && node.getBoundingClientRect().height > 0);
      if (!trigger) return;
      trigger.focus({preventScroll: true});
      trigger.scrollIntoView({block: 'center'});
    };
    restoreFocus();
    requestAnimationFrame(() => {
      if (document.activeElement === document.body) restoreFocus();
    });
  }

  document.addEventListener('keydown', event => {
    if (event.key !== 'Escape' || event.defaultPrevented || !isBuilderOpen()) return;
    // Leave Escape to an open app/payment dialog before closing the inline builder.
    if ([...document.querySelectorAll('dialog[open], [role="dialog"][aria-modal="true"], afterpay-modal[open]')].some(node => node.getClientRects().length && getComputedStyle(node).visibility !== 'hidden')) return;
    if (state.builderShell?.contains(event.target)) {
      event.preventDefault();
      closeBuilder();
    }
  });

  function setBuilderVisibility(visible) {
    if (!isFramePdp()) return;

    refreshBuilderRefs();
    setBaseProductVisibility(!visible);

    if (visible) prepareBuilderNavigation();
    if (state.builderShell) state.builderShell.style.display = visible ? '' : 'none';
    if (state.builder) {
      if (visible) {
        releaseBuilderCollapsedState();
      } else {
        state.builder.style.display = 'none';
        restoreAfterpayPlacement();
        restoreZipWidget();
      }
    }
    document.documentElement.classList.toggle('gp-frame-builder-open', !!visible);
    document.dispatchEvent(new CustomEvent('gp:frame-builder-visibility', {
      detail: { visible: !!visible }
    }));
    if (visible) {
      ensureKlaviyoReviewHostVisible();
      scheduleKlaviyoReviewsRecovery();
    } else {
      ensureHeroStars();
    }
    if (state.desktopStickyUpdate) state.desktopStickyUpdate();
    if (state.desktopMediaStickyUpdate) state.desktopMediaStickyUpdate();
  }

  function getFrameFormWrapper() {
    return utils.qs('[data-product-form]');
  }

  function getFrameProductForm() {
    const wrapper = getFrameFormWrapper();
    return wrapper ? utils.qs('form[action*="/cart/add"]', wrapper) : null;
  }

  function ensureFormInput(form, name, value) {
    if (!form || !name) return null;

    let input = form.querySelector(`input[name="${name}"]`);
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      form.appendChild(input);
    }

    if (value !== undefined && value !== null) {
      input.value = String(value);
    }

    return input;
  }

  function getFallbackVariantId(wrapper, form) {
    const explicitInput = form?.querySelector('input[name="id"]');
    if (explicitInput?.value) return explicitInput.value;

    const variantInput = wrapper?.querySelector('[data-variants]');
    if (variantInput?.value && variantInput.value !== 'not-selected') {
      return variantInput.value;
    }

    const inventoryPayload = wrapper?.getAttribute('data-variant-inventory');
    if (!inventoryPayload) return '';

    try {
      const inventory = JSON.parse(inventoryPayload);
      return inventory?.[0]?.id ? String(inventory[0].id) : '';
    } catch (_) {
      return '';
    }
  }

  function buildTrustMicrocopy() {
    const trustItems = Array.isArray(frameConfig.primary_trust_items) && frameConfig.primary_trust_items.length
      ? frameConfig.primary_trust_items
      : ['Free Shipping', '10-Year Warranty', 'Easy Assembly'];

    const wrapper = document.createElement('div');
    wrapper.className = 'gp-frame-trust-microcopy';
    wrapper.setAttribute('data-frame-trust-microcopy', '');
    wrapper.innerHTML = `
      <div class="gp-frame-trust-microcopy__band">
        ${trustItems.map((item) => `<span>${item}</span>`).join('')}
      </div>
    `;
    return wrapper;
  }

  function getCompactHeroBenefitIconMarkup(index) {
    if (index === 0) {
      return `
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
          <path d="M3 7h10v8H3z"></path>
          <path d="M13 10h4l3 3v2h-7z"></path>
          <circle cx="7.5" cy="17.5" r="1.5"></circle>
          <circle cx="17.5" cy="17.5" r="1.5"></circle>
        </svg>
      `;
    }

    if (index === 1) {
      return `
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
          <path d="M12 3l7 3v5c0 4.5-3 7.5-7 10-4-2.5-7-5.5-7-10V6l7-3z"></path>
          <path d="M9.5 12.5l1.8 1.8 3.7-4"></path>
        </svg>
      `;
    }

    return `
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
        <path d="M14.7 6.3a4 4 0 0 0-5.4 5.8l-5 5 2.6 2.6 5-5a4 4 0 0 0 5.8-5.4l-2.6 2.1-1.9-1.9z"></path>
      </svg>
    `;
  }

  function buildCompactHeroBenefits() {
    const items = Array.isArray(frameConfig.compact_prebuilder_benefits) && frameConfig.compact_prebuilder_benefits.length
      ? frameConfig.compact_prebuilder_benefits
      : ['Free Shipping', '10-Year Frame Warranty', 'Easy DIY Install'];

    const wrapper = document.createElement('div');
    wrapper.className = 'gp-frame-hero-benefits';
    wrapper.setAttribute('data-frame-hero-benefits', '');
    wrapper.innerHTML = items.map((label, index) => `
      <div class="gp-frame-hero-benefit-card"${index === 0 ? ' aria-label="Free shipping applies on frame orders only and excludes NT and TAS." tabindex="0"' : ''}>
        <span class="gp-frame-hero-benefit-card__icon" aria-hidden="true">
          ${getCompactHeroBenefitIconMarkup(index)}
        </span>
        <span class="gp-frame-hero-benefit-card__label">${utils.escapeHtml(label)}</span>
        ${index === 0 ? '<span class="gp-frame-hero-benefit-card__tooltip" role="tooltip">Available on eligible metro frame orders.</span>' : ''}
      </div>
    `).join('');
    return wrapper;
  }

  function buildCompactIncludedPanel() {
    const items = Array.isArray(frameConfig.compact_included_items) ? frameConfig.compact_included_items.filter(Boolean) : [];
    if (!items.length) return null;

    const panel = document.createElement('div');
    panel.className = 'gp-frame-included-panel';
    panel.setAttribute('data-frame-included-panel', '');
    panel.innerHTML = `
      <p class="gp-frame-included-panel__eyebrow">What’s Included</p>
      <div class="gp-frame-included-panel__grid">
        ${items.map((item) => {
          const itemTitle = typeof item === 'object' && item !== null ? item.title : item;
          const rawQuantity = typeof item === 'object' && item !== null ? item.quantity : 1;
          const quantity = Number(rawQuantity) > 1 ? Number(rawQuantity) : 1;
          const label = `${utils.toTitleCase(String(itemTitle || ''))}${quantity > 1 ? ` x ${quantity}` : ''}`;
          return `
            <div class="gp-frame-included-panel__item">
              <span class="gp-frame-included-panel__check" aria-hidden="true">✓</span>
              <span class="gp-frame-included-panel__text">${utils.escapeHtml(label)}</span>
            </div>
          `;
        }).join('')}
      </div>
    `;
    return panel;
  }

  function ensureCompactHeroPanels(purchaseDetails, buttonsWrap) {
    if (!isFramePdp() || !purchaseDetails || !buttonsWrap) return false;

    let benefits = utils.qs('[data-frame-hero-benefits]', purchaseDetails);
    const optionalAddons = utils.qs('.cubby-addons--compact', purchaseDetails);
    if (optionalAddons && buttonsWrap.previousElementSibling !== optionalAddons) {
      purchaseDetails.insertBefore(optionalAddons, buttonsWrap);
    }
    const includedAnchor = optionalAddons || buttonsWrap;
    let includedPanel = utils.qs('[data-frame-included-panel]', purchaseDetails);
    if (!includedPanel) {
      const existingIncludedPanel = purchaseDetails.parentElement?.querySelector('[data-frame-included-panel]');
      if (existingIncludedPanel) {
        includedPanel = existingIncludedPanel;
        purchaseDetails.insertBefore(includedPanel, includedAnchor);
      }
    }
    if (!includedPanel) {
      includedPanel = buildCompactIncludedPanel();
      if (includedPanel) {
        purchaseDetails.insertBefore(includedPanel, includedAnchor);
      }
    }

    if (includedPanel && includedAnchor.previousElementSibling !== includedPanel) {
      purchaseDetails.insertBefore(includedPanel, includedAnchor);
    }

    if (!benefits) {
      benefits = buildCompactHeroBenefits();
      buttonsWrap.insertAdjacentElement('afterend', benefits);
    }

    if (benefits && benefits.previousElementSibling !== buttonsWrap) {
      buttonsWrap.insertAdjacentElement('afterend', benefits);
    }

    return true;
  }

  function ensureBuilderTrustMicrocopy() {
    return true;
  }

  function getChooseAdventureItems() {
    return Array.isArray(frameConfig.choose_adventure)
      ? frameConfig.choose_adventure.filter(Boolean).slice(0, 3)
      : [];
  }

  function hasChooseAdventurePreviewOffer() {
    return isFramePdp() && Number(frameConfig.choose_adventure_count || 0) > 0 && getChooseAdventureItems().length > 0;
  }

  function normalizeAdventureOptionTitle(value) {
    return String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
  }

  function formatAdventureDisplayTitle(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/(^|[\s(/-])([a-z])/g, (_, prefix, letter) => `${prefix}${letter.toUpperCase()}`);
  }

  function findChooseAdventureItem(option) {
    if (!option) return null;

    const label = utils.qs('.cpb-option-label, .gp-frame-addon-tile__title', option)?.textContent
      || option.getAttribute('data-gp-adventure-label')
      || option.textContent
      || '';
    const normalizedLabel = normalizeAdventureOptionTitle(label);
    if (normalizedLabel) {
      const matchedItem = getChooseAdventureItems().find((candidate) => {
        const normalizedTitle = normalizeAdventureOptionTitle(candidate.title);
        return normalizedTitle === normalizedLabel
          || normalizedTitle.includes(normalizedLabel)
          || normalizedLabel.includes(normalizedTitle)
          || (normalizedLabel.includes('ninja') && normalizedTitle.includes('ninja'));
      });
      if (matchedItem) return matchedItem;
    }

    // CPB can render the adventure cards without a usable text label. Keep
    // the metafield order as a deterministic fallback so the configured
    // product title and frame-specific variant price are still rendered.
    const category = option.closest('.cpb-choose-adventure');
    if (!category) return null;
    const card = option.matches('.cpb-option-type-img')
      ? option
      : utils.qs('.cpb-option-type-img', option);
    const cards = Array.from(utils.qsa('.cpb-option-type-img', category));
    const index = card ? cards.indexOf(card) : -1;
    return index >= 0 ? getChooseAdventureItems()[index] || null : null;
  }

  function filterChooseAdventureOptions(category) {
    if (!category) return 0;

    const selectedItems = getChooseAdventureItems();
    const selectedLabels = selectedItems
      .map((item) => normalizeAdventureOptionTitle(item?.title || item?.handle))
      .filter(Boolean);
    const options = Array.from(utils.qsa('.cpb-option-type-img', category));

    options.forEach((option) => {
      const labels = [
        utils.qs('.cpb-option-label', option)?.textContent,
        option.getAttribute('aria-label'),
        option.getAttribute('data-gp-addon-label'),
        utils.qs('img', option)?.getAttribute('alt'),
        option.textContent
      ]
        .map(normalizeAdventureOptionTitle)
        .filter(Boolean);
      const visible = selectedLabels.some((selectedLabel) => labels.some((label) => (
        label === selectedLabel || label.includes(selectedLabel) || selectedLabel.includes(label)
      )));

      option.hidden = !visible;
      option.setAttribute('aria-hidden', String(!visible));
      if (visible) {
        option.style.removeProperty('display');
      } else {
        option.style.setProperty('display', 'none', 'important');
      }
    });

    return options.filter((option) => !option.hidden).length;
  }

  function hydrateChooseAdventureOptions(category) {
    if (!category || !hasChooseAdventurePreviewOffer()) return;

    utils.qsa('.cpb-option-type-img', category).forEach((option) => {
      if (option.hidden || option.getAttribute('aria-hidden') === 'true') return;

      const item = findChooseAdventureItem(option);
      if (!item?.image) return;

      option.setAttribute('data-gp-adventure-label', String(item.title || '').trim());
      let image = utils.qs('.gp-frame-cpb-adventure-image', option);
      if (!image) {
        image = document.createElement('img');
        image.className = 'gp-frame-cpb-adventure-image';
        image.alt = String(item.title || '').trim();
        image.loading = 'eager';
        option.insertBefore(image, option.firstChild);
      }
      image.src = String(item.image);
    });
  }

  function getAdventureOptionTile(option) {
    if (!option) return null;
    return option.classList.contains('gp-frame-addon-tile')
      ? option
      : utils.qs('.gp-frame-addon-tile', option) || option;
  }

  function ensureCpbStepMarker(titleInner, className, text) {
    if (!titleInner) return null;
    let marker = utils.qs('.' + className, titleInner);
    if (!marker) {
      marker = document.createElement('span');
      marker.className = className;
      titleInner.prepend(marker);
    }
    if (marker.textContent !== text) marker.textContent = text;
    return marker;
  }

  function clearCpbStepMarkers(category) {
    if (!category) return;
    category.classList.remove('gp-frame-cpb-step-1', 'gp-frame-cpb-step-2');
    utils.qsa('.gp-frame-cpb-step-number, .gp-frame-cpb-free-badge, .gp-frame-cpb-optional-badge', category)
      .forEach((node) => node.remove());
  }

  function updateCpbStepMarkers(adventureCategory, shouldShow) {
    if (!state.builder) return;

    const categories = Array.from(utils.qsa('.cpb-category', state.builder));

    categories.forEach((category) => {
      const isAdventureStep = shouldShow && category === adventureCategory;
      category.classList.remove('gp-frame-cpb-step-1', 'gp-frame-cpb-step-2');
      utils.qsa('.gp-frame-cpb-step-number, .gp-frame-cpb-optional-badge', category)
        .forEach((node) => node.remove());

      const titleInner = utils.qs('.cpb-category-title__inner, .cpb-category-title', category);
      if (!isAdventureStep || !titleInner) {
        utils.qsa('.gp-frame-cpb-free-badge', category)
          .forEach((node) => node.remove());
        return;
      }

      titleInner.classList.add('gp-frame-cpb-title-row');
      titleInner.closest('.cpb-category-title')?.classList.add('gp-frame-cpb-title-row');

      let badge = utils.qs('.gp-frame-cpb-free-badge', titleInner);
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'gp-frame-cpb-free-badge';
        titleInner.appendChild(badge);
      }
      if (badge.textContent !== '1 INCLUDED FREE') badge.textContent = '1 INCLUDED FREE';
    });
  }

  function syncChooseAdventureSelectionPresentation(category) {
    if (!category || !hasChooseAdventurePreviewOffer()) return;

    // CPB owns the actual selection and cart state. Remove only presentation
    // nodes left by an older script version; do not add a replacement control.
    utils.qsa('.gp-frame-adventure-check, .gp-frame-adventure-selected-label, [data-gp-adventure-summary]', category)
      .forEach((node) => node.remove());
    return;

    // Selection is communicated by the border/checkmark and summary; the
    // extra text badge makes the compact CPB cards noisy.
    utils.qsa('.gp-frame-adventure-selected-label', category).forEach((node) => node.remove());

    const seenTiles = new Set();
    const entries = Array.from(utils.qsa('.cpb-option-type-img, .cpb-option', category))
      .map((option) => {
        const tile = getAdventureOptionTile(option);
        if (!tile || seenTiles.has(tile)) return null;
        seenTiles.add(tile);
        const item = findChooseAdventureItem(tile) || findChooseAdventureItem(option);
        return item ? { option, tile, item } : null;
      })
      .filter(Boolean);

    let selectedEntry = null;
    entries.forEach(({ option, tile, item }) => {
      const nodes = [option, tile, tile.parentElement].filter((node, index, list) => node && list.indexOf(node) === index);
      const selected = nodes.some((node) => isAddonNodeSelected(node));

      // CPB applies a taller active-state height to the option after a click.
      // Normalize the live option and its card wrapper on every refresh so the
      // selection state changes only the border/checkmark, never the geometry.
      nodes
        .filter((node) => !node.classList.contains('cpb-category-options-container') && !node.classList.contains('cpb-option__wrapper'))
        .forEach((node) => {
          node.style.setProperty('height', 'auto', 'important');
          node.style.setProperty('min-height', '0', 'important');
          node.style.setProperty('max-height', 'none', 'important');
          node.style.setProperty('block-size', 'auto', 'important');
          node.style.setProperty('grid-template-rows', '96px auto', 'important');
          node.style.setProperty('padding', '8px', 'important');
        });

      // The .cpb-option is only CPB's layout wrapper. The actual visible card
      // is the inner .gp-frame-addon-tile; styling both as cards makes them
      // overlap because the wrapper's padding is counted twice.
      const cardNodes = [tile].filter(Boolean);
      // The builder can be narrower than the browser viewport (for example,
      // when the product media and builder columns share the page). Base the
      // card geometry on the panel width instead of the viewport breakpoint.
      const isFluidAdventureLayout = true;
      const fluidCardGap = 10;
      const fluidCardCount = Math.max(entries.length, 1);
      const adventureOptions = category.querySelector('.cpb-category-options-container');
      const adventureWrapper = adventureOptions?.querySelector('.cpb-option__wrapper');
      [adventureOptions, adventureWrapper].filter(Boolean).forEach((node) => {
        node.style.setProperty('box-sizing', 'border-box', 'important');
        node.style.setProperty('width', '100%', 'important');
        node.style.setProperty('min-width', '0', 'important');
        node.style.setProperty('max-width', 'none', 'important');
        node.style.setProperty('padding-left', '0', 'important');
        node.style.setProperty('padding-right', '0', 'important');
      });

      // Do not calculate from the options wrapper: some CPB versions shrink
      // that wrapper to its contents, so percentage widths make the cards
      // wider than the visible adventure panel. Measure the panel instead.
      let fluidCardWidth = `calc((100% - ${(fluidCardCount - 1) * fluidCardGap}px) / ${fluidCardCount})`;
      if (isFluidAdventureLayout) {
        const categoryStyle = window.getComputedStyle(category);
        const horizontalInsets =
          parseFloat(categoryStyle.paddingLeft || '0') +
          parseFloat(categoryStyle.paddingRight || '0') +
          parseFloat(categoryStyle.borderLeftWidth || '0') +
          parseFloat(categoryStyle.borderRightWidth || '0');
        const categoryWidth = category.getBoundingClientRect().width || category.clientWidth;
        const availableWidth = categoryWidth - horizontalInsets - ((fluidCardCount - 1) * fluidCardGap);
        if (availableWidth > 0) {
          fluidCardWidth = `${Math.floor(availableWidth / fluidCardCount)}px`;
        }
      }
      const adventureCardWidth = isFluidAdventureLayout ? fluidCardWidth : '190px';
      cardNodes.forEach((node) => {
        node.style.setProperty('box-sizing', 'border-box', 'important');
        node.style.setProperty('width', adventureCardWidth, 'important');
        node.style.setProperty('min-width', isFluidAdventureLayout ? '0' : '190px', 'important');
        node.style.setProperty('max-width', isFluidAdventureLayout ? 'none' : '190px', 'important');
        node.style.setProperty('flex', isFluidAdventureLayout ? `0 0 ${fluidCardWidth}` : '0 0 190px', 'important');
        node.style.setProperty('height', '154px', 'important');
        node.style.setProperty('min-height', '154px', 'important');
        node.style.setProperty('max-height', '154px', 'important');
        node.style.setProperty('block-size', '154px', 'important');
        node.style.setProperty('grid-template-rows', '96px minmax(0, auto)', 'important');
        node.style.setProperty('align-content', 'start', 'important');
        node.style.setProperty('align-items', 'start', 'important');
        node.style.setProperty('justify-content', 'stretch', 'important');
        node.style.setProperty('margin', '0', 'important');
        node.style.setProperty('overflow', 'hidden', 'important');

        // CPB can move the selected image with its active-state styles. Keep
        // the image viewport and image itself fixed inside the card instead.
        utils.qsa('.gp-frame-addon-tile__image, .gp-frame-addon-tile__image img, .gp-frame-cpb-adventure-image', node)
          .forEach((imageNode) => {
            imageNode.style.setProperty('box-sizing', 'border-box', 'important');
            imageNode.style.setProperty('position', 'static', 'important');
            imageNode.style.setProperty('inset', 'auto', 'important');
            imageNode.style.setProperty('margin', '0', 'important');
            imageNode.style.setProperty('transform', 'none', 'important');
            imageNode.style.setProperty('overflow', 'hidden', 'important');
            imageNode.style.setProperty('object-fit', 'contain', 'important');
            imageNode.style.setProperty('object-position', 'center', 'important');
        });
      });

      if (option && option !== tile && option.classList.contains('cpb-option')) {
        option.style.setProperty('box-sizing', 'border-box', 'important');
        option.style.setProperty('width', adventureCardWidth, 'important');
        option.style.setProperty('min-width', isFluidAdventureLayout ? '0' : '190px', 'important');
        option.style.setProperty('max-width', isFluidAdventureLayout ? 'none' : '190px', 'important');
        option.style.setProperty('flex', isFluidAdventureLayout ? `0 0 ${fluidCardWidth}` : '0 0 190px', 'important');
        option.style.setProperty('height', '154px', 'important');
        option.style.setProperty('min-height', '154px', 'important');
        option.style.setProperty('max-height', '154px', 'important');
        option.style.setProperty('block-size', '154px', 'important');
        option.style.setProperty('padding', '0', 'important');
        option.style.setProperty('border', '0', 'important');
        option.style.setProperty('border-radius', '0', 'important');
        option.style.setProperty('background', 'transparent', 'important');
        option.style.setProperty('box-shadow', 'none', 'important');
        option.style.setProperty('display', 'block', 'important');
        option.style.setProperty('margin', '0', 'important');
        option.style.setProperty('overflow', 'visible', 'important');
      }

      tile.classList.toggle('gp-frame-adventure-selected', selected);

      let check = utils.qs('.gp-frame-adventure-check', tile);
      if (!check) {
        check = document.createElement('span');
        check.className = 'gp-frame-adventure-check';
        tile.appendChild(check);
      }
      const checkText = selected ? '✓' : '';
      if (check.textContent !== checkText) check.textContent = checkText;
      check.classList.toggle('gp-frame-adventure-check--selected', selected);
      if (check.getAttribute('aria-hidden') !== 'true') check.setAttribute('aria-hidden', 'true');

      if (selected && !selectedEntry) selectedEntry = { item };
    });

    const optionsContainer = utils.qs('.cpb-category-options-container', category);
    if (!optionsContainer) return;

    // Selection is communicated by the card border and green checkmark.
    // Remove the older confirmation bar if CPB or a previous refresh created it.
    utils.qsa('[data-gp-adventure-summary]', category).forEach((node) => node.remove());

    // Size the adventure section to its actual content: two-card offers use
    // the summary width (or the two-card row, whichever is wider), while
    // three-card offers use exactly the three-card row width.
    const isFluidAdventureLayout = window.matchMedia?.('(max-width: 1100px)').matches;
    if (!isFluidAdventureLayout) {
      const cardWidth = 190;
      const cardGap = 10;
      const cardsWidth = (entries.length * cardWidth) + (Math.max(entries.length - 1, 0) * cardGap);
      // Give the two-option header enough room for the heading and free badge
      // without touching the fixed card row width.
      const headerWidth = entries.length === 2 ? 460 : cardsWidth;
      const contentWidth = Math.max(cardsWidth, headerWidth);
      category.style.setProperty('width', `${contentWidth + 20}px`, 'important');
      optionsContainer.style.setProperty('width', `${cardsWidth}px`, 'important');
      optionsContainer.style.setProperty('display', 'block', 'important');
      optionsContainer.style.setProperty('grid-template-columns', 'none', 'important');
      const wrapper = utils.qs('.cpb-option__wrapper', optionsContainer);
      if (wrapper) {
        wrapper.style.setProperty('display', 'flex', 'important');
        wrapper.style.setProperty('flex-direction', 'row', 'important');
        wrapper.style.setProperty('flex-wrap', 'nowrap', 'important');
        wrapper.style.setProperty('align-items', 'stretch', 'important');
        wrapper.style.setProperty('justify-content', 'flex-start', 'important');
        wrapper.style.setProperty('gap', `${cardGap}px`, 'important');
        wrapper.style.setProperty('grid-template-columns', 'none', 'important');
        wrapper.style.setProperty('width', `${cardsWidth}px`, 'important');
      }
    } else {
      category.style.setProperty('width', '100%', 'important');
      optionsContainer.style.setProperty('display', 'block', 'important');
      optionsContainer.style.setProperty('grid-template-columns', 'none', 'important');
      const wrapper = utils.qs('.cpb-option__wrapper', optionsContainer);
      if (wrapper) {
        wrapper.style.setProperty('display', 'flex', 'important');
        wrapper.style.setProperty('flex-direction', 'row', 'important');
        wrapper.style.setProperty('flex-wrap', 'nowrap', 'important');
        wrapper.style.setProperty('gap', '10px', 'important');
        wrapper.style.setProperty('grid-template-columns', 'none', 'important');
        wrapper.style.setProperty('width', '100%', 'important');
      }
    }
  }

  function decorateChooseAdventureCategories() {
    if (!state.builder) return [];
    const categories = Array.from(utils.qsa('.cpb-category', state.builder)).filter((category) => {
      const title = utils.qs('.cpb-category-title__inner, .cpb-category-title', category)?.textContent || '';
      const normalizedTitle = String(title).replace(/\s+/g, ' ').trim().toLowerCase();
      const isAdventure = category.classList.contains('cpb-choose-adventure')
        || normalizedTitle === 'choose your adventure'
        || normalizedTitle === 'choose your free add-on';
      if (isAdventure) category.classList.add('cpb-choose-adventure');
      return isAdventure;
    });

    const shouldShow = hasChooseAdventurePreviewOffer();
    categories.forEach((category) => {
      category.hidden = !shouldShow;
      category.setAttribute('aria-hidden', String(!shouldShow));
      category.style.display = shouldShow ? '' : 'none';
      const visibleOptionCount = shouldShow ? filterChooseAdventureOptions(category) : 0;
      if (shouldShow) hydrateChooseAdventureOptions(category);
      category.classList.toggle('gp-frame-cpb-adventure-options--two', visibleOptionCount === 2);
      category.classList.toggle('gp-frame-cpb-adventure-options--three', visibleOptionCount === 3);
    });

    updateCpbStepMarkers(categories.find((category) => category.classList.contains('cpb-choose-adventure')), shouldShow);

    if (!state.chooseAdventureObserver) {
      state.chooseAdventureObserver = new MutationObserver(() => {
        decorateChooseAdventureCategories();
      });
      state.chooseAdventureObserver.observe(state.builder, {
        childList: true,
        subtree: true
      });
    }

    return categories;
  }

  function buildChooseAdventureCardMarkup(item) {
    const title = String(item.title || '').trim();
    const image = String(item.image || '').trim();
    const compareAt = Number(item.compare_at_cents || 0);
    return `
      <div class="gp-frame-choose-adventure-teaser__card">
        ${image ? `<img class="gp-frame-choose-adventure-teaser__card-image" src="${utils.escapeHtml(image)}" alt="${utils.escapeHtml(title)}" loading="eager" width="96" height="72">` : ''}
        <span class="gp-frame-choose-adventure-teaser__card-title">${utils.escapeHtml(title)}</span>
        ${compareAt > 0 ? `<span class="gp-frame-choose-adventure-teaser__card-pricing"><span class="gp-frame-choose-adventure-teaser__card-price">${utils.escapeHtml(formatMoneyCents(compareAt))}</span><span class="gp-frame-choose-adventure-teaser__card-free">Free</span></span>` : ''}
      </div>
    `;
  }

  function syncChooseAdventureTeaser() {
    if (!hasChooseAdventurePreviewOffer()) return false;
    const buttonsWrap = utils.qs('.gp-frame-purchase-details__buttons, .purchase-details__buttons');
    if (!buttonsWrap) return false;
    const purchaseDetails = buttonsWrap.closest('.purchase-details') || buttonsWrap.parentElement;
    const teaser = utils.qs('[data-frame-choose-adventure-teaser]', purchaseDetails || document);
    if (!teaser) return false;

    const items = getChooseAdventureItems();
    const maxValue = items.reduce((max, item) => Math.max(max, Number(item.compare_at_cents || 0)), 0);
    const markup = `
      <div class="gp-frame-choose-adventure-teaser__header">
        <div class="gp-frame-choose-adventure-teaser__intro">
          <h3 class="gp-frame-choose-adventure-teaser__heading">Choose any 1 of these add-ons free</h3>
          <p class="gp-frame-choose-adventure-teaser__subtitle">Customise your frame and choose your favorite at no extra cost.</p>
        </div>
        ${maxValue > 0 ? `<span class="gp-frame-choose-adventure-teaser__value">Up to ${utils.escapeHtml(formatMoneyCents(maxValue))} value</span>` : ''}
      </div>
      <div class="gp-frame-choose-adventure-teaser__grid">${items.map(buildChooseAdventureCardMarkup).join('')}</div>
      <button type="button" class="button action_button gp-frame-open-builder gp-frame-choose-adventure-teaser__builder" data-frame-open-builder data-frame-id="${utils.escapeHtml(String(frameConfig.frame_id || ''))}">
        <span class="text">Customise your frame &amp; choose yours →</span>
      </button>
    `;
    if (teaser.innerHTML.trim() !== markup.trim()) teaser.innerHTML = markup;
    bindRevealButtons();
    teaser.hidden = isBuilderOpen();
    return !teaser.hidden;
  }

  function syncFreeAddonTeaser() {
    if (!isFramePdp()) return false;

    if (hasChooseAdventurePreviewOffer()) {
      const purchaseDetails = utils.qs('.purchase-details');
      utils.qsa('[data-frame-free-addon-teaser]', purchaseDetails || document).forEach((node) => node.remove());
      return syncChooseAdventureTeaser();
    }

    const buttonsWrap = utils.qs('.gp-frame-purchase-details__buttons, .purchase-details__buttons');
    if (!buttonsWrap) return false;
    const purchaseDetails = buttonsWrap.closest('.purchase-details') || buttonsWrap.parentElement;

    const builderNote = utils.qs('.gp-frame-open-builder-note', buttonsWrap);
    const primaryCta = utils.qs('[data-frame-primary-cta], .button--add-to-cart', buttonsWrap);
    if (!builderNote && !primaryCta) return false;

    const teaserNodes = utils.qsa('[data-frame-free-addon-teaser]', purchaseDetails || document);
    let teaser = teaserNodes[0] || null;
    if (teaserNodes.length > 1) {
      teaserNodes.slice(1).forEach((node) => node.remove());
    }

    if (!teaser) {
      teaser = document.createElement('div');
      teaser.className = 'gp-frame-free-addon-teaser gp-frame-free-addon-offer';
      teaser.setAttribute('data-frame-free-addon-teaser', '');
      teaser.hidden = true;
      (primaryCta || buttonsWrap.firstElementChild || buttonsWrap).insertAdjacentElement('beforebegin', teaser);
    }

    const hasServerRenderedContent = teaser.innerHTML.trim().length > 0;
    if (!isBuilderOpen() && hasServerRenderedContent) {
      const serverCardCount = teaser.querySelectorAll('.gp-frame-free-addon-offer__card').length;
      teaser.classList.toggle('gp-frame-free-addon-offer--multi', serverCardCount > 1);
      setFreeAddonOfferCountClass(teaser, serverCardCount || 1);
      reorderPrebuilderHeroBlocks(teaser);
      teaser.hidden = false;
      return true;
    }

    // Before the builder opens, render the hero offer from static frame/accessory
    // data only. This keeps the hero stable and avoids waiting on CPB state.
    if (!isBuilderOpen()) {
      const immediateData = getImmediateFreeAddonOfferData();
      const markup = buildFreeAddonOfferMarkup(immediateData);
      if (!markup) {
        teaser.hidden = true;
        teaser.innerHTML = '';
        teaser.classList.remove('gp-frame-free-addon-offer--multi');
        setFreeAddonOfferCountClass(teaser, 0);
        return false;
      }

      if (teaser.innerHTML.trim() !== markup.trim()) {
        teaser.innerHTML = markup;
      }
      const immediateCount = Array.isArray(immediateData?.items) ? immediateData.items.length : 1;
      teaser.classList.toggle('gp-frame-free-addon-offer--multi', immediateCount > 1);
      setFreeAddonOfferCountClass(teaser, immediateCount);
      reorderPrebuilderHeroBlocks(teaser);
      teaser.hidden = false;
      return true;
    }

    if (!state.builder) {
      refreshBuilderRefs();
    }

    const configuredItems = getConfiguredFreeAddonItems();
    if (configuredItems.length > 1) {
      teaser.classList.add('gp-frame-free-addon-offer--multi');
      setFreeAddonOfferCountClass(teaser, configuredItems.length);
      teaser.innerHTML = buildFreeAddonOfferMarkup({
        items: configuredItems,
        compareAt: Number(frameConfig.free_addons_total_value_cents || 0)
          || configuredItems.reduce((total, item) => total + Number(item.compareAt || 0), 0)
      });
      teaser.hidden = false;
      return true;
    }

    const freeContext = getFreeAddonContext();
    if (!hasConfiguredFreeAddonOffer() && !freeContext) {
      teaser.hidden = true;
      teaser.innerHTML = '';
      teaser.classList.remove('gp-frame-free-addon-offer--multi');
      setFreeAddonOfferCountClass(teaser, 0);
      return false;
    }

    const fallbackLabel = String(frameConfig.free_addon_label || '').trim();
    const fallbackValueCents = Number(frameConfig.free_addon_compare_at_cents || 0)
      || (Number(frameConfig.free_addon_value || 0) > 0 ? Number(frameConfig.free_addon_value || 0) * 100 : 0);
    const addonTitle = String(freeContext?.record?.title || fallbackLabel || '').trim();
    const compareAt = Number(freeContext?.compareAt || fallbackValueCents || 0);
    const image = String(freeContext?.image || freeContext?.record?.image || frameConfig.free_addon_image || '').trim();

    if (!addonTitle && !compareAt) {
      teaser.hidden = true;
      teaser.innerHTML = '';
      teaser.classList.remove('gp-frame-free-addon-offer--multi');
      setFreeAddonOfferCountClass(teaser, 0);
      return false;
    }

    teaser.classList.remove('gp-frame-free-addon-offer--multi');
    setFreeAddonOfferCountClass(teaser, 1);
    teaser.innerHTML = buildFreeAddonOfferMarkup({
      title: addonTitle,
      subtitle: 'Included with your frame - limited time',
      compareAt,
      image
    });
    teaser.hidden = false;
    return true;
  }

  function syncBackorderStatusPill() {
    if (!isFramePdp()) return false;

    const text = getBackorderStatusText();
    utils.qsa('[data-frame-builder-backorder-status]').forEach((node) => {
      if (!text) {
        node.remove();
        return;
      }
      const label = node.querySelector('[data-backorder-status-label]') || node.querySelector('span');
      if (label) label.textContent = text;
    });

    if (!text || !isBuilderOpen()) return !!text;

    if (!state.panelsContainer) {
      refreshBuilderRefs();
    }

    const parent = state.panelsContainer || state.builder;
    if (!parent) return false;

    let pill = utils.qs('[data-frame-builder-backorder-status]', parent);
    if (!pill) {
      pill = createBackorderStatusPill('gp-frame-builder-backorder-status');
      if (!pill) return false;
      pill.setAttribute('data-frame-builder-backorder-status', '');
    }

    const anchor = utils.qs('.cpb-product-actions', parent)
      || utils.qs('.gp-frame-selected-addon', parent)
      || utils.qs('.cpb-custom-product-description-container', parent)
      || parent.firstElementChild;

    if (anchor && pill.nextElementSibling !== anchor) {
      anchor.insertAdjacentElement('beforebegin', pill);
    } else if (!pill.parentNode) {
      parent.appendChild(pill);
    }

    return true;
  }

  function reorderPrebuilderHeroBlocks(teaser) {
    if (!isFramePdp() || !teaser || isBuilderOpen()) return false;

    const purchaseDetails = teaser.closest('.purchase-details');
    const includedPanel = purchaseDetails && utils.qs('[data-frame-included-panel]', purchaseDetails);
    const benefits = purchaseDetails && utils.qs('[data-frame-hero-benefits]', purchaseDetails);
    const target = includedPanel || benefits;
    if (!target) return false;

    if (teaser.nextElementSibling !== target) {
      target.insertAdjacentElement('beforebegin', teaser);
    }

    return true;
  }

  function getFreeAddonRecord() {
    if (!isFramePdp()) return null;

    const configuredItems = getConfiguredFreeAddonItems();
    if (configuredItems.length) {
      const configuredItem = configuredItems[0];
      const record = findAccessoryRecord(configuredItem.title)
        || accessoryIndex.find((item) => item && item.handle === configuredItem.handle);

      return {
        ...(record || {}),
        title: configuredItem.title || record?.title || '',
        handle: configuredItem.handle || record?.handle || '',
        image: configuredItem.image || record?.image || '',
        variant_id: configuredItem.variantId || Number(record?.variant_id || 0),
        compare_at_price: configuredItem.compareAt || Number(record?.compare_at_price || 0),
        price: configuredItem.compareAt || Number(record?.price || 0)
      };
    }

    const freeContext = getFreeAddonContext();
    if (freeContext?.record) return freeContext.record;

    if (!hasConfiguredFreeAddonOffer()) return null;

    const configuredTitle = String(frameConfig.free_addon_label || '').replace(/^free\s+/i, '').trim();
    const configuredVariantId = getConfiguredFreeAddonVariantId({
      title: configuredTitle,
      handle: String(frameConfig.free_addon_handle || '').trim(),
      variant_id: frameConfig.free_addon_variant_id
    });
    if (configuredTitle || configuredVariantId) {
      return {
        title: configuredTitle,
        handle: String(frameConfig.free_addon_handle || '').trim(),
        image: String(frameConfig.free_addon_image || '').trim(),
        variant_id: configuredVariantId,
        compare_at_price: Number(frameConfig.free_addon_compare_at_cents || 0),
        price: Number(frameConfig.free_addon_compare_at_cents || 0)
      };
    }

    const fallbackLabel = String(frameConfig.free_addon_label || '').trim();
    if (!fallbackLabel) return null;

    return findAccessoryRecord(fallbackLabel);
  }

  function getFreeAddonCartRecords() {
    if (!isFramePdp()) return [];

    // A sold-out gift would make the cart refuse the frame too.
    const configuredItems = getConfiguredFreeAddonItems()
      .filter((item) => Number(item.variantId || 0) > 0 && item.available)
      .map((item) => ({
        title: item.title,
        handle: item.handle,
        image: item.image,
        variant_id: Number(item.variantId || 0),
        compare_at_price: Number(item.compareAt || 0),
        price: Number(item.compareAt || 0)
      }));

    if (configuredItems.length) return configuredItems;

    const fallbackRecord = getFreeAddonRecord();
    return fallbackRecord ? [fallbackRecord] : [];
  }

  function getSelectedFreeAddonCartItems(quantity, usedVariantIds) {
    if (!state.builder) return [];

    const selectedEntries = getSelectedAddonEntries()
      .filter((entry) => isConfiguredFreeAddonEntry(entry));
    const items = [];

    selectedEntries.forEach((entry) => {
      const optionId = entry.node.getAttribute('data-option-id')
        || entry.node.querySelector('[data-option-id]')?.getAttribute('data-option-id')
        || '';
      const optionData = getCpbOptionData(optionId);
      const sku = optionData?.sku || optionData?.variant_sku || optionData?.variantSku || '';
      const record = findAccessoryRecordBySku(sku) || findAccessoryRecord(entry.label);
      const variantId = Number(optionData?.variant_id || optionData?.variantId || 0)
        || getAccessoryVariantId(record, sku);
      if (!variantId || usedVariantIds.has(variantId)) return;

      usedVariantIds.add(variantId);
      items.push({
        id: variantId,
        quantity,
        properties: {
          '_gp_bonus_item': 'true',
          '_gp_bonus_parent': 'Frame bundle'
        }
      });
    });

    return items;
  }
  function getFrameCartItems(activeForm, activeWrapper, options = {}) {
    const includeFreeAddons = options.includeFreeAddons !== false;
    if (!activeForm || !activeWrapper) return [];

    const baseVariantId = Number(getFallbackVariantId(activeWrapper, activeForm) || 0);
    if (!baseVariantId) return [];

    const quantity = Math.max(1, Number(activeForm.querySelector('input[name="quantity"]')?.value || 1) || 1);
    const baseItem = { id: baseVariantId, quantity };
    const statusText = getBackorderStatusText();
    if (statusText) {
      baseItem.properties = { Status: statusText };
    }
    const items = [baseItem];
    // The cart links a free gift to its frame by the product's own title (assets/utilities.js).
    const frameTitle = String(frameConfig.product_title || '').trim()
      || utils.qs('.gp-frame-product__information .product-title.title, .product-title.title')?.textContent?.trim() || '';
    const usedVariantIds = new Set([baseVariantId]);
    activeForm.querySelectorAll('.cubby-addons--compact [data-cubby-addon-variant]:checked').forEach((checkbox) => {
      const variantId = Number(checkbox.getAttribute('data-cubby-addon-variant') || 0);
      if (!variantId || usedVariantIds.has(variantId)) return;
      usedVariantIds.add(variantId);
      items.push({ id: variantId, quantity: 1 });
    });
    const selectedFreeAddonItems = getSelectedFreeAddonCartItems(quantity, usedVariantIds);
    items.push(...selectedFreeAddonItems);

    if (includeFreeAddons && !selectedFreeAddonItems.length) {
      getFreeAddonCartRecords().forEach((freeAddonRecord) => {
      const freeAddonVariantId = Number(freeAddonRecord?.variant_id || 0);
      if (!freeAddonVariantId || usedVariantIds.has(freeAddonVariantId)) return;

      usedVariantIds.add(freeAddonVariantId);
      items.push({
        id: freeAddonVariantId,
        quantity,
        properties: {
          '_gp_bonus_item': 'true',
          '_gp_bonus_parent': frameTitle || 'Frame bundle'
        }
      });
      });
    }

    return items;
  }

  function addFrameCartItems(items) {
    return fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ items })
    }).then((response) => {
      if (!response.ok) throw response;
      return response.json();
    });
  }

  function fetchCartState() {
    return fetch('/cart.js', {
      credentials: 'same-origin',
      headers: {
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    }).then((response) => {
      if (!response.ok) throw response;
      return response.json();
    });
  }

  function getCartItemCount() {
    return fetchCartState().then((cart) => Number(cart?.item_count || 0));
  }

  function getCpbProductState() {
    const stores = window.CPB?.App?.stores || {};
    const store = Object.values(stores).find((candidate) => typeof candidate?.getState === 'function');
    return store?.getState?.()?.Product || null;
  }

  function getCpbOptionData(optionId) {
    if (!optionId) return null;

    const productState = getCpbProductState();
    const panels = Array.isArray(productState?.panels) ? productState.panels : [];
    for (const panel of panels) {
      const categories = Array.isArray(panel?.categories) ? panel.categories : [];
      for (const category of categories) {
        const options = Array.isArray(category?.options) ? category.options : [];
        const option = options.find((candidate) => String(candidate?.id || '') === String(optionId));
        if (option) return option;
      }
    }

    return null;
  }

  function findAccessoryRecordBySku(sku) {
    const normalizedSku = utils.normalizeText(sku);
    if (!normalizedSku) return null;

    return accessoryIndex.find((record) => {
      const variants = Array.isArray(record?.variants) ? record.variants : [];
      return variants.some((variant) => utils.normalizeText(variant?.sku) === normalizedSku);
    }) || null;
  }

  function addAccessoryRecordFromProduct(product) {
    if (!product?.handle) return null;

    const variants = Array.isArray(product.variants)
      ? product.variants.map((variant) => ({
        id: Number(variant?.id || 0),
        title: variant?.title || '',
        sku: variant?.sku || '',
        available: variant?.available !== false,
        price: Number(variant?.price || 0),
        compare_at_price: Number(variant?.compare_at_price || 0)
      }))
      : [];

    const record = {
      title: product.title || '',
      handle: product.handle || '',
      variant_id: Number(product.selected_or_first_available_variant?.id || variants.find((variant) => variant.available)?.id || variants[0]?.id || 0),
      tags: Array.isArray(product.tags) ? product.tags : [],
      benefit: '',
      price: Number(product.price || variants[0]?.price || 0),
      compare_at_price: Number(product.compare_at_price || variants[0]?.compare_at_price || 0),
      variants,
      image: product.featured_image || ''
    };

    const existingIndex = accessoryIndex.findIndex((item) => item?.handle === record.handle);
    if (existingIndex >= 0) {
      accessoryIndex[existingIndex] = record;
    } else {
      accessoryIndex.push(record);
    }

    return record;
  }

  function queueAccessoryRecordFetch(handle) {
    const productHandle = String(handle || '').trim();
    if (!productHandle || accessoryRecordFetches.has(productHandle)) return;

    const productUrl = `/products/${encodeURIComponent(productHandle)}.js`;
    const request = fetch(productUrl, { credentials: 'same-origin' })
      .then((response) => (response.ok ? response.json() : null))
      .then((product) => {
        if (!product) return null;
        const record = addAccessoryRecordFromProduct(product);
        if (record) scheduleAddonTileRefresh(0);
        return record;
      })
      .catch(() => null);

    accessoryRecordFetches.set(productHandle, request);
  }

  function getFrameVariantKeywords() {
    const frameId = String(frameConfig.frame_id || '').trim();
    if (frameId === 'compact') return ['compact'];
    if (frameId === 'medium') return ['medium', 'classic'];
    if (frameId === 'large') return ['large', 'adventurer'];
    if (frameId === 'xl') return ['xl', 'explorer'];
    return [];
  }

  function getAccessoryVariantId(record, sku) {
    if (!record) return 0;

    const variants = Array.isArray(record.variants) ? record.variants : [];
    const normalizedSku = utils.normalizeText(sku);
    if (normalizedSku && variants.length) {
      const skuMatch = variants.find((variant) => utils.normalizeText(variant?.sku) === normalizedSku && variant?.available !== false)
        || variants.find((variant) => utils.normalizeText(variant?.sku) === normalizedSku);
      if (skuMatch?.id) return Number(skuMatch.id || 0);
    }

    const keywords = getFrameVariantKeywords();
    if (keywords.length && variants.length) {
      const frameMatch = variants.find((variant) => {
        if (variant?.available === false) return false;
        const title = utils.normalizeText(variant?.title);
        return keywords.some((keyword) => title.includes(keyword));
      });
      if (frameMatch?.id) return Number(frameMatch.id || 0);
    }

    return Number(record.variant_id || variants.find((variant) => variant?.available !== false)?.id || variants[0]?.id || 0);
  }

  function getAccessoryVariantRecord(record, sku) {
    if (!record) return null;

    const variants = Array.isArray(record.variants) ? record.variants : [];
    const normalizedSku = utils.normalizeText(sku);
    if (normalizedSku && variants.length) {
      const skuMatch = variants.find((variant) => utils.normalizeText(variant?.sku) === normalizedSku && variant?.available !== false)
        || variants.find((variant) => utils.normalizeText(variant?.sku) === normalizedSku);
      if (skuMatch) return skuMatch;
    }

    const variantId = getAccessoryVariantId(record, sku);
    return variants.find((variant) => Number(variant?.id || 0) === variantId) || null;
  }

  function isConfiguredFreeAddonEntry(entry) {
    if (!entry) return false;
    if (entry.isFree) return true;

    const normalizedLabel = utils.normalizeText(entry.label || '');
    if (!normalizedLabel) return false;

    const freeLabels = getConfiguredFreeAddonItems()
      .map((item) => utils.normalizeText(item.title || item.handle || ''))
      .filter(Boolean);
    const fallbackLabel = utils.normalizeText(frameConfig.free_addon_label || '');
    if (fallbackLabel) freeLabels.push(fallbackLabel);

    return freeLabels.some((freeLabel) =>
      normalizedLabel === freeLabel
      || normalizedLabel.includes(freeLabel)
      || freeLabel.includes(normalizedLabel)
    );
  }

  function getSelectedBuilderAddonCartItems(quantity, usedVariantIds) {
    // In the builder, the selected free option is the source of truth.
    // Only the non-builder setup button injects the configured default add-on.
    const selectedEntries = getSelectedAddonEntries();
    const items = [];

    selectedEntries.forEach((entry) => {
      const optionId = entry.node.getAttribute('data-option-id') || entry.node.querySelector('[data-option-id]')?.getAttribute('data-option-id') || '';
      const optionData = getCpbOptionData(optionId);
      const record = findAccessoryRecordBySku(optionData?.sku) || findAccessoryRecord(entry.label);
      const variantId = getAccessoryVariantId(record, optionData?.sku);
      if (!variantId || usedVariantIds.has(variantId)) return;

      usedVariantIds.add(variantId);
      items.push({
        id: variantId,
        quantity,
        properties: {
          '_gp_cpb_fallback': 'true',
          '_gp_cpb_option': entry.label,
          ...(isConfiguredFreeAddonEntry(entry) ? { '_gp_bonus_item': 'true', '_gp_bonus_parent': 'Frame bundle' } : {})
        }
      });
    });

    return items;
  }

  function getBuilderFallbackCartItems() {
    const activeForm = getFrameProductForm();
    const activeWrapper = getFrameFormWrapper();
    const items = getFrameCartItems(activeForm, activeWrapper, { includeFreeAddons: false });
    const usedVariantIds = new Set(items.map((item) => Number(item.id || 0)).filter(Boolean));
    const quantity = Math.max(1, Number(activeForm?.querySelector('input[name="quantity"]')?.value || 1) || 1);

    return items.concat(getSelectedBuilderAddonCartItems(quantity, usedVariantIds));
  }

  function addBuilderFallbackCart(button, options = {}) {
    const redirect = options.redirect !== false;
    const items = getBuilderFallbackCartItems();
    if (!items.length) {
      setBuilderCartButtonBusy(button, false);
      return Promise.resolve(false);
    }

    return addFrameCartItems(items)
      .then(() => {
        if (redirect) window.location.href = '/cart';
        return true;
      })
      .catch(() => {
        setBuilderCartButtonBusy(button, false);
        return false;
      });
  }

  function enhanceHeroPricingUi() {
    if (!isFramePdp()) return false;

    const priceContainer = utils.qs('.gp-frame-product__information .modal_price');
    if (!priceContainer) return false;

    const priceUi = utils.qs('[data-price-ui]', priceContainer) || priceContainer;
    utils.qsa('shopify-payment-terms', priceUi).forEach((node) => node.remove());

    const currentMoney = utils.qs('.price .money, .price--sale .money, [data-price] .money', priceUi);
    const compareMoney = utils.qs('.compare-at-price .money, [data-compare-at-price] .money', priceUi);
    if (!currentMoney) return false;

    priceUi.classList.add('gp-frame-standard-price-ui');
    currentMoney.closest('.price, .price--sale, [data-price]')?.classList.add('gp-frame-standard-price-ui__current');
    compareMoney?.closest('.compare-at-price, [data-compare-at-price]')?.classList.add('gp-frame-standard-price-ui__compare');

    const currentAmount = parseAmount(currentMoney.textContent || '');
    const compareAmount = compareMoney ? parseAmount(compareMoney.textContent || '') : null;

    if (currentAmount !== null) {
      const text = formatPrice(String(currentAmount));
      if (currentMoney.textContent !== text) currentMoney.textContent = text;
    }

    if (compareMoney && compareAmount !== null) {
      const text = formatPrice(String(compareAmount));
      if (compareMoney.textContent !== text) compareMoney.textContent = text;
    }

    let savingsBadge = utils.qs('.gp-frame-hero-price-savings', priceUi);
    if (!savingsBadge) {
      savingsBadge = document.createElement('span');
      savingsBadge.className = 'gp-frame-hero-price-savings';
      priceUi.appendChild(savingsBadge);
    }

    // Only the frame's own discount: the free gift is shown separately.
    if (currentAmount !== null && compareAmount !== null && compareAmount > currentAmount) {
      const text = `Save ${formatMoneyCents(Math.round((compareAmount - currentAmount) * 100))}`;
      if (savingsBadge.textContent !== text) savingsBadge.textContent = text;
      savingsBadge.hidden = false;
      savingsBadge.removeAttribute('aria-hidden');
    } else {
      if (savingsBadge.textContent) savingsBadge.textContent = '';
      savingsBadge.hidden = true;
      savingsBadge.setAttribute('aria-hidden', 'true');
    }

    if (state.heroPriceObserver || priceUi.dataset.gpHeroPriceObserved === 'true') return true;

    state.heroPriceObserver = new MutationObserver(() => {
      if (state.heroPriceRafId) return;
      state.heroPriceRafId = requestAnimationFrame(() => {
        state.heroPriceRafId = null;
        enhanceHeroPricingUi();
        const heroPriceText = findHeroPriceText();
        if (heroPriceText) syncStandardAfterpayAmount(heroPriceText);
      });
    });

    state.heroPriceObserver.observe(priceUi, {
      childList: true,
      subtree: true,
      characterData: true
    });
    priceUi.dataset.gpHeroPriceObserved = 'true';

    return true;
  }

  function normalizeBuilderDiscountUi() {
    if (!isFramePdp()) return false;
    syncBuilderCartButtonTotal();

    const wrappers = utils.qsa('.cpb-custom-rrp, .cpb-custom-product-savings', state.builder || document);
    if (!wrappers.length) return syncBuilderCompareAtUi();

    wrappers.forEach((node) => {
      const text = String(node.textContent || '').replace(/\s+/g, ' ').trim();
      const hasMoneyValue = /\$\s*\d/.test(text);
      const hasSavingsLabel = /save/i.test(text);
      const shouldShow = hasMoneyValue || hasSavingsLabel;

      node.style.display = shouldShow ? '' : 'none';
      node.toggleAttribute('hidden', !shouldShow);
      if (!shouldShow) {
        node.setAttribute('aria-hidden', 'true');
      } else {
        node.removeAttribute('aria-hidden');
      }
    });

    syncBuilderCompareAtUi();

    return true;
  }

  function scheduleBuilderCompareAtSync() {
    if (!isFramePdp()) return;

    if (Array.isArray(state.builderDiscountSyncTimeouts)) {
      state.builderDiscountSyncTimeouts.forEach(clearTimeout);
    }

    state.builderDiscountSyncTimeouts = [0, 40, 140, 320].map((delay) => setTimeout(() => {
      normalizeBuilderDiscountUi();
    }, delay));
  }

  function getSelectedPaidAddonTotals() {
    const configuredFreeAddonLabels = getConfiguredFreeAddonItems()
      .map((item) => utils.normalizeText(item.title || item.handle || ''))
      .filter(Boolean);
    const fallbackFreeAddonLabel = utils.normalizeText(frameConfig.free_addon_label || '');
    if (fallbackFreeAddonLabel) configuredFreeAddonLabels.push(fallbackFreeAddonLabel);

    const entries = getSelectedAddonEntries().filter((entry) => {
      if (entry.isFree) return false;
      const normalizedLabel = utils.normalizeText(entry.label);
      return !configuredFreeAddonLabels.some((freeLabel) => normalizedLabel === freeLabel || normalizedLabel.includes(freeLabel));
    });

    return entries.reduce((totals, entry) => {
      const optionId = entry.node.getAttribute('data-option-id') || entry.node.querySelector('[data-option-id]')?.getAttribute('data-option-id') || '';
      const optionData = getCpbOptionData(optionId);
      const pricing = extractPriceDataFromNode(entry.node);
      const record = findAccessoryRecordBySku(optionData?.sku) || findAccessoryRecord(entry.label);
      const variantRecord = getAccessoryVariantRecord(record, optionData?.sku);
      const currentPrice = Number(pricing.currentPrice ?? variantRecord?.price ?? record?.price ?? 0);
      const compareAt = Number(pricing.compareAt ?? variantRecord?.compare_at_price ?? record?.compare_at_price ?? currentPrice);

      if (currentPrice > 0) totals.current += currentPrice;
      if (compareAt > 0) totals.compareAt += compareAt;

      return totals;
    }, { current: 0, compareAt: 0 });
  }

  function syncBuilderCompareAtUi() {
    if (!isFramePdp() || !state.builder) return false;

    const basePrice = Number(frameConfig.base_price_cents || 0);
    const baseCompareAt = Number(frameConfig.base_compare_at_cents || 0);
    if (!basePrice || !baseCompareAt || baseCompareAt <= basePrice) return false;

    const currentPriceEl = utils.qs('.cpb-custom-product-price', state.builder);
    const currentAmount = parseAmount(currentPriceEl?.textContent || '');
    if (currentAmount === null) return false;

    const currentCents = Math.round(currentAmount * 100);
    const addonTotals = getSelectedPaidAddonTotals();
    const targetCompareAt = baseCompareAt + addonTotals.compareAt;
    const savings = targetCompareAt - currentCents;
    if (savings <= 0) return false;

    let compareNode = utils.qs('.cpb-custom-rrp', state.builder);
    // The native fallback has valid prices before CPB creates its discount nodes.
    if (!compareNode) {
      const priceContainer = currentPriceEl.closest('.cpb-custom-product-price-container');
      if (!priceContainer) return false;
      compareNode = document.createElement('span');
      compareNode.className = 'cpb-custom-rrp';
      priceContainer.appendChild(compareNode);
    }
    let savingsNode = utils.qs('.cpb-custom-product-savings', state.builder);
    if (savingsNode && compareNode.contains(savingsNode)) {
      compareNode.after(savingsNode);
    }
    const compareText = formatMoneyCents(targetCompareAt);
    if (compareNode.textContent !== compareText) compareNode.textContent = compareText;
    compareNode.classList.add('gp-builder-regular-price');
    compareNode.style.display = '';
    compareNode.hidden = false;
    compareNode.removeAttribute('aria-hidden');
    if (!savingsNode) {
      savingsNode = document.createElement('span');
      savingsNode.className = 'cpb-custom-product-savings';
      compareNode.after(savingsNode);
    }
    const savingsText = `SAVE ${formatMoneyCents(savings)}`;
    if (savingsNode.textContent !== savingsText) savingsNode.textContent = savingsText;
    savingsNode.style.display = '';
    savingsNode.hidden = false;
    savingsNode.removeAttribute('aria-hidden');

    return true;
  }

  function ensureFrameHeroPurchaseUi() {
    if (!isFramePdp()) return false;

    const wrapper = getFrameFormWrapper();
    const form = getFrameProductForm();
    if (!wrapper || !form) return false;

    const fallbackVariantId = getFallbackVariantId(wrapper, form);
    if (fallbackVariantId) {
      ensureFormInput(form, 'id', fallbackVariantId);
    }
    ensureFormInput(form, 'quantity', form.querySelector('input[name="quantity"]')?.value || 1);

    // CPB can inject its legacy button outside the native cart form.
    utils.qsa('.personalize-it--js').forEach((button) => {
      button.hidden = true;
      button.setAttribute('aria-hidden', 'true');
      button.tabIndex = -1;
    });

    let purchaseDetails = utils.qs('.purchase-details', wrapper);
    if (!purchaseDetails) {
      purchaseDetails = document.createElement('div');
      purchaseDetails.className = `purchase-details has-quantity-box-${document.body.classList.contains('quantity-box--hidden') ? 'false' : 'true'}`;

      const insertionPoint = utils.qs('#qbk-embed-placeholder-product-recommendation, [data-surface-pick-up], .surface-pick-up__modal', wrapper);
      if (insertionPoint) {
        wrapper.insertBefore(purchaseDetails, insertionPoint);
      } else {
        wrapper.appendChild(purchaseDetails);
      }
    }

    let buttonsWrap = utils.qs('.purchase-details__buttons', purchaseDetails);
    if (!buttonsWrap) {
      buttonsWrap = document.createElement('div');
      buttonsWrap.className = 'purchase-details__buttons purchase-details__spb--false gp-frame-purchase-details__buttons';
      purchaseDetails.insertBefore(buttonsWrap, purchaseDetails.firstChild);
    } else {
      buttonsWrap.classList.add('gp-frame-purchase-details__buttons');
    }

    ensureCompactHeroPanels(purchaseDetails, buttonsWrap);

    const frameProductIsStockOut = isFrameStockOut();
    const isChooseAdventurePromo = hasChooseAdventurePreviewOffer();
    const hideFrameCartCta = isChooseAdventurePromo && !frameProductIsStockOut;
    // Keep the label the theme rendered (it names the frame, e.g. "Add The XL to Cart"), unless the frame sets its own.
    const renderedCartLabel = buttonsWrap.querySelector('button[name="add"]')?.getAttribute('data-label') || '';
    const configuredCartLabel = String(frameConfig.order_button_label || '').trim();
    const cartButtonLabel = frameProductIsStockOut
      ? 'Notify me when in stock'
      : (configuredCartLabel || (renderedCartLabel && !/notify/i.test(renderedCartLabel) ? renderedCartLabel : 'Add This Setup to Cart'));
    const builderButtonLabel = isChooseAdventurePromo
      ? 'Customise your frame & choose yours →'
      : 'Customise Your Setup →';
    const builderButtonNote = isChooseAdventurePromo || frameConfig.frame_id === 'medium'
      ? ''
      : 'Add <strong>swings</strong>, <strong>hoops</strong>, <strong>flying fox</strong> and more with the interactive customiser.';

    /* --- SECONDARY CTA: Buy base frame only --- */
    const cartAction = window.PXUTheme?.theme_settings?.cart_action;
    const shouldUseAjaxSubmit = !frameProductIsStockOut && (cartAction === 'mini_cart' || cartAction === 'drawer');
    const applyCartButtonState = (button, useSubmitTrigger = false) => {
      if (!button) return;
      button.classList.remove('gp-frame-primary-cta--sold-out');
      button.disabled = false;
      button.setAttribute('aria-disabled', 'false');
      button.classList.toggle('gp-frame-notify-cta', frameProductIsStockOut);
      button.toggleAttribute('data-gp-bis-trigger', frameProductIsStockOut);
      button.toggleAttribute('data-frame-notify-cta', frameProductIsStockOut);
      if (frameProductIsStockOut) button.setAttribute('aria-haspopup', 'dialog');
      else button.removeAttribute('aria-haspopup');
      button.setAttribute('data-label', cartButtonLabel);
      button.innerHTML = `<span class="text">${cartButtonLabel}</span>`;

      if (frameProductIsStockOut) {
        button.type = 'button';
        button.removeAttribute('data-add-to-cart-trigger');
        button.setAttribute('data-frame-sold-out-cta', '');
        return;
      }

      button.removeAttribute('data-frame-sold-out-cta');
      if (useSubmitTrigger) {
        button.setAttribute('data-add-to-cart-trigger', '');
      } else {
        button.removeAttribute('data-add-to-cart-trigger');
      }
    };
    let cartButton = utils.qs('[data-frame-primary-cta]', buttonsWrap);
    if (!cartButton && !hideFrameCartCta) {
      cartButton = document.createElement('button');
      cartButton.type = 'button';
      cartButton.name = 'add';
      cartButton.className = `button${shouldUseAjaxSubmit ? ' ajax-submit' : ''} action_button button--add-to-cart`;
      cartButton.setAttribute('data-frame-primary-cta', '');
      buttonsWrap.appendChild(cartButton);
    } else if (cartButton) {
      cartButton.type = 'button';
      cartButton.className = `button${shouldUseAjaxSubmit ? ' ajax-submit' : ''} action_button button--add-to-cart`;
    }
    if (cartButton) {
      applyCartButtonState(cartButton, true);
    }

    let builderButton = isChooseAdventurePromo
      ? utils.qs('[data-frame-choose-adventure-teaser] [data-frame-open-builder]', purchaseDetails)
      : utils.qs('[data-frame-open-builder]', buttonsWrap);
    if (!builderButton) {
      builderButton = document.createElement('button');
      builderButton.type = 'button';
      builderButton.className = 'button action_button button--secondary gp-frame-open-builder';
      builderButton.setAttribute('data-frame-open-builder', '');
      builderButton.setAttribute('data-frame-id', frameConfig.frame_id || '');
      builderButton.innerHTML = `<span class="text">${builderButtonLabel}</span>`;
      buttonsWrap.appendChild(builderButton);
    } else {
      builderButton.className = isChooseAdventurePromo
        ? 'button action_button gp-frame-open-builder gp-frame-choose-adventure-teaser__builder'
        : 'button action_button button--secondary gp-frame-open-builder';
      builderButton.innerHTML = `<span class="text">${builderButtonLabel}</span>`;
    }

    let builderNote = utils.qs('.gp-frame-open-builder-note', buttonsWrap);
    if (!isChooseAdventurePromo && builderButtonNote) {
      if (!builderNote) {
        builderNote = document.createElement('p');
        builderNote.className = 'gp-frame-open-builder-note';
        buttonsWrap.appendChild(builderNote);
      }
      builderNote.innerHTML = builderButtonNote;
    } else if (builderNote) {
      builderNote.remove();
      builderNote = null;
    }

    if (!isChooseAdventurePromo) {
      if (builderNote && buttonsWrap.firstElementChild !== builderNote) {
        buttonsWrap.insertBefore(builderNote, buttonsWrap.firstChild);
      }
      if (builderNote && builderNote.nextElementSibling !== builderButton) {
        buttonsWrap.insertBefore(builderButton, builderNote.nextSibling);
      } else if (!builderNote && buttonsWrap.firstElementChild !== builderButton) {
        buttonsWrap.insertBefore(builderButton, buttonsWrap.firstChild);
      }
      if (cartButton && builderButton.nextElementSibling !== cartButton) {
        buttonsWrap.insertBefore(cartButton, builderButton.nextSibling);
      }
    }

    let compareLink = utils.qs('.gp-frame-compare-link', buttonsWrap);
    if (!compareLink) {
      compareLink = document.createElement('a');
      compareLink.className = 'gp-frame-compare-link';
      buttonsWrap.appendChild(compareLink);
    }
    compareLink.href = '#compare-frames';
    compareLink.textContent = 'Not sure which frame? Compare all →';
    const benefits = utils.qs('[data-frame-hero-benefits]', purchaseDetails);
    const compareAnchor = benefits || cartButton || builderButton;
    if (compareAnchor && compareLink.previousElementSibling !== compareAnchor) {
      compareAnchor.insertAdjacentElement('afterend', compareLink);
    }

    syncFreeAddonTeaser();
    syncBackorderStatusPill();

    const bindPrimaryCta = (button) => {
      if (!button || button.dataset.gpSubmitBound === 'true') return;

      button.addEventListener('click', (event) => {
        event.preventDefault();

        if (frameProductIsStockOut || button.disabled || button.getAttribute('aria-disabled') === 'true') return;
        if (button.dataset.gpBusy === 'true') return;

        const activeForm = getFrameProductForm();
        const activeWrapper = getFrameFormWrapper();
        if (!activeForm || !activeWrapper) return;

        const variantId = getFallbackVariantId(activeWrapper, activeForm);
        if (variantId) ensureFormInput(activeForm, 'id', variantId);
        ensureFormInput(activeForm, 'quantity', activeForm.querySelector('input[name="quantity"]')?.value || 1);

        const submitBaseForm = () => {
          if (shouldUseAjaxSubmit && window.PXUTheme?.jsAjaxCart?.addToCart && window.jQuery) {
            window.PXUTheme.jsAjaxCart.addToCart(window.jQuery(activeForm));
            return;
          }

          if (typeof activeForm.requestSubmit === 'function') {
            activeForm.requestSubmit();
          } else {
            activeForm.submit();
          }
        };

        const items = getFrameCartItems(activeForm, activeWrapper);
        if (items.length > 1) {
          button.dataset.gpBusy = 'true';
          button.classList.add('gp-frame-primary-cta--busy');
          button.setAttribute('aria-busy', 'true');

          addFrameCartItems(items)
            .then(() => {
              if (shouldUseAjaxSubmit && window.PXUTheme?.jsAjaxCart) {
                utils.setTrackedTimeout(() => {
                  delete button.dataset.gpBusy;
                  button.classList.remove('gp-frame-primary-cta--busy');
                  button.removeAttribute('aria-busy');
                }, 700);
                window.PXUTheme.jsAjaxCart.showDrawer?.();
                window.PXUTheme.jsAjaxCart.updateView?.();
                return;
              }

              window.location.href = '/cart';
            })
            .catch(() => {
              delete button.dataset.gpBusy;
              button.classList.remove('gp-frame-primary-cta--busy');
              button.removeAttribute('aria-busy');
              submitBaseForm();
            });
          return;
        }

        submitBaseForm();
      });

      button.dataset.gpSubmitBound = 'true';
    };

    bindPrimaryCta(cartButton);
    utils.qsa('[data-frame-sticky-primary-cta]').forEach((stickyButton) => {
      if (hideFrameCartCta) {
        stickyButton.hidden = true;
        stickyButton.setAttribute('aria-hidden', 'true');
        return;
      }
      applyCartButtonState(stickyButton);
      bindPrimaryCta(stickyButton);
    });

    utils.qs('[data-frame-trust-microcopy]', purchaseDetails)?.remove();

    return true;
  }

  function ensureCustomPriceContainer() {
    // Only one theme price presentation may survive repeated CPB mount events.
    utils.qsa('.cpb-custom-prices-container', state.builder).forEach(node => {
      if (node !== state.customPricesContainer) node.remove();
    });
    if (state.customPricesContainer) return true;

    const realPrice = findBestPriceEl();
    if (!realPrice) return false;

    const container = document.createElement('div');
    container.className = 'cpb-custom-prices-container';

    const priceWrapper = document.createElement('div');
    priceWrapper.className = 'cpb-custom-product-price-container';
    priceWrapper.style.display = 'block';
    priceWrapper.style.width = '100%';

    const customPrice = document.createElement('span');
    customPrice.className = 'cpb-custom-product-price';
    customPrice.setAttribute('role', 'status');
    customPrice.setAttribute('aria-live', 'polite');
    customPrice.setAttribute('aria-atomic', 'true');
    customPrice.textContent = formatPrice(realPrice.textContent);

    priceWrapper.appendChild(customPrice);
    container.appendChild(priceWrapper);

    state.customPricesContainer = container;
    state.builder.dataset.customProductPrice = 'true';

    const updatePrice = () => {
      if (state.rafId) return;
      state.rafId = requestAnimationFrame(() => {
        state.rafId = null;
        const currentPriceEl = findBestPriceEl() || realPrice;
        const currentText = currentPriceEl.textContent;
        const text = formatPrice(currentText);
        if (customPrice.textContent !== text) customPrice.textContent = text;
        syncAfterpayAmount(currentText);
        normalizeBuilderDiscountUi();
      });
    };

    state.priceObserver = new MutationObserver(updatePrice);
    state.priceObserver.observe(realPrice, {
      childList: true,
      characterData: true,
      subtree: true
    });

    if (!state.builderDiscountObserver) {
      state.builderDiscountObserver = new MutationObserver((mutations) => {
        const shouldSync = mutations.some((mutation) => {
          const target = mutation.target;
          return target && (
            target.classList?.contains('cpb-custom-product-price-container')
            || target.classList?.contains('cpb-custom-rrp')
            || target.closest?.('.cpb-custom-product-price-container')
          );
        });

        if (shouldSync) scheduleBuilderCompareAtSync();
      });

      state.builderDiscountObserver.observe(container, {
        childList: true,
        characterData: true,
        subtree: true
      });
    }

    scheduleBuilderCompareAtSync();

    return true;
  }

  function refreshBuilderPaypalMessage() {
    const previous = document.querySelector('[data-cpb-payment-messaging] .junior-payment-messaging__paypal');
    if (!previous) return;
    // PayPal disconnects its observers when CPB reparents an initialized widget.
    // Render a fresh target in the final visible location, then attribute updates work normally.
    const target = previous.cloneNode(false);
    target.removeAttribute('data-pp-id');
    previous.replaceWith(target);
    const render = () => {
      if (target.isConnected && window.paypal?.Messages) {
        window.paypal.Messages().render(target).catch(() => {});
      }
    };
    if (window.paypal?.Messages) render();
    else document.querySelector('script[src*="paypal.com/sdk/js"]')?.addEventListener('load', render, { once: true });
  }

  function placeAfterpayWidget(mountId) {
    if (state.mountedId !== mountId) return false;
    if (isFramePdp() && !isBuilderOpen()) return false;
    const unified = document.querySelector('gp-bnpl[data-context="builder"]');
    if (unified) {
      const priceWrapper = document.querySelector('.cpb-custom-product-price-container');
      if (!priceWrapper) return false;
      let row = document.querySelector('.cpb-payment-inline');
      if (!row) {
        row = document.createElement('div'); row.className = 'cpb-payment-inline';
        priceWrapper.insertAdjacentElement('afterend', row);
      }
      if (unified.parentElement !== row) row.append(unified);
      const price = findBestPriceEl();
      if (price) syncAfterpayAmount(price.textContent);
      return true;
    }


    const paymentRow = document.querySelector('[data-cpb-payment-messaging]');
    if (isFramePdp() && paymentRow) {
      const priceWrapper = document.querySelector('.cpb-custom-product-price-container');
      if (!priceWrapper) return false;
      let paymentInline = document.querySelector('.cpb-payment-inline');
      if (!paymentInline) {
        paymentInline = document.createElement('div');
        paymentInline.className = 'cpb-payment-inline';
        priceWrapper.insertAdjacentElement('afterend', paymentInline);
      }
      if (paymentRow.parentElement !== paymentInline) {
        paymentInline.appendChild(paymentRow);
        refreshBuilderPaypalMessage();
      }
      const currentPriceEl = findBestPriceEl();
      if (currentPriceEl) syncAfterpayAmount(currentPriceEl.textContent);
      return true;
    }

    const priceWrapper = document.querySelector('.cpb-custom-product-price-container');
    if (!priceWrapper) return false;

    let paymentInline = document.querySelector('.cpb-payment-inline');
    if (!paymentInline) {
      paymentInline = document.createElement('div');
      paymentInline.className = 'cpb-payment-inline';
      priceWrapper.insertAdjacentElement('afterend', paymentInline);
    }

    let afterpayMount = document.querySelector('.cpb-afterpay-mount');
    if (!afterpayMount) {
      afterpayMount = document.createElement('div');
      afterpayMount.className = 'cpb-afterpay-mount';
    }

    const placement = document.querySelector('[data-cpb-afterpay-placement]');
    if (!placement) return false;

    if (afterpayMount.parentElement !== paymentInline) {
      const brandStrip = paymentInline.querySelector('.cpb-payment-brand-strip');
      paymentInline.insertBefore(afterpayMount, brandStrip || paymentInline.firstChild);
    }
    if (placement.parentElement !== afterpayMount) afterpayMount.appendChild(placement);
    placement.style.display = 'block';

    const currentPriceEl = findBestPriceEl();
    if (currentPriceEl) syncAfterpayAmount(currentPriceEl.textContent);
    scheduleAfterpayShadowFix();

    return true;
  }

  function moveDescription(mountId) {
    const description = utils.qs('.product-block--description');
    const cpbDescription = utils.qs('.cpb-product-description');

    if (!description || !cpbDescription) return false;
    if (state.mountedId !== mountId) return false;

    cpbDescription.replaceChildren(description);
    return true;
  }

  function placeElements(mountId) {
    if (state.mountedId !== mountId) return false;
    if (isFramePdp() && !isBuilderOpen()) return false;

    // On mobile the title lives in the preview header after the first mount.
    const productTitle = utils.qs('.cpb-product-title', state.builder);
    if (!productTitle) return false;
    const normalizedProductTitle = normalizeFrameDisplayTitle(productTitle.textContent);
    if (normalizedProductTitle && normalizedProductTitle !== productTitle.textContent.trim()) {
      productTitle.textContent = normalizedProductTitle;
    }

    /* Build a Klaviyo-style star rating for the builder view.
       We can't clone the Klaviyo widget because it's dynamically rendered
       and clones don't re-initialize. Instead, read the rating data once
       from the Klaviyo button's aria-label and build a static replica. */
    let ratingTarget = utils.qs('.gp-frame-builder-rating', state.builder);
    utils.qsa('.gp-frame-builder-rating', state.builder).forEach(node => {
      if (node !== ratingTarget) node.remove();
    });

    if (!ratingTarget) {
      var data = getKlaviyoRatingData();
      if (data && data.val > 0 && data.count > 0) {
        var starsHtml = buildStaticStarHtml(data.val, data.count);
        ratingTarget = document.createElement('div');
        ratingTarget.className = 'gp-frame-builder-rating';
        ratingTarget.innerHTML =
          '<a href="#klaviyo-product-reviews-wrapper" aria-label="' + data.val + ' out of 5 stars" style="display:inline-flex;align-items:center;gap:2px;text-decoration:none;">' +
            starsHtml +
          '</a>' +
          '<a href="#klaviyo-product-reviews-wrapper" class="gp-frame-hero-rating__count" style="text-decoration:underline;text-decoration-color:rgba(74,93,107,0.3);text-underline-offset:2px;">' +
            data.count + ' reviews</a>';
      }
    }

    ensureCustomPriceContainer();
    normalizeBuilderDiscountUi();

    if (state.customPricesContainer && !state.customPricesContainer.isConnected) {
      productTitle.insertAdjacentElement('afterend', state.customPricesContainer);
    } else if (state.customPricesContainer && state.customPricesContainer.previousElementSibling !== productTitle) {
      productTitle.insertAdjacentElement('afterend', state.customPricesContainer);
    }

    if (ratingTarget && ratingTarget.previousElementSibling !== productTitle) {
      productTitle.insertAdjacentElement('afterend', ratingTarget);
    }

    syncBuilderMobileHeaderLayout();

    return true;
  }

  function ensureBuilderMobileHeaderHost() {
    if (!state.builder) return null;

    const previewContainer = utils.qs('.cpb-preview-container', state.builder);
    if (!previewContainer) return null;

    let host = utils.qs('.gp-frame-builder-mobile-header', previewContainer);
    if (host) return host;

    host = document.createElement('div');
    host.className = 'gp-frame-builder-mobile-header';

    const previewAnchor = utils.qs('.cpb-preview-wrapper, .cpb-preview-elements, .cpb-preview-sticky', previewContainer);
    if (previewAnchor) {
      previewAnchor.insertAdjacentElement('beforebegin', host);
    } else {
      previewContainer.insertAdjacentElement('afterbegin', host);
    }

    return host;
  }

  function syncBuilderMobileHeaderLayout() {
    if (!isFramePdp() || !state.builder || !state.panelsContainer) return false;

    const previewContainer = utils.qs('.cpb-preview-container', state.builder);
    const productTitle = utils.qs('.cpb-product-title', state.builder);
    const priceContainer = utils.qs('.cpb-custom-prices-container', state.builder);
    const paymentInline = utils.qs('.cpb-payment-inline', state.builder);
    const rating = utils.qs('.gp-frame-builder-rating', state.builder);
    if (!previewContainer || !productTitle || !priceContainer) return false;

    if (!state.builderHeaderPlaceholder?.isConnected) {
      state.builderHeaderPlaceholder = document.createComment('gp-frame-builder-header-placeholder');
      const firstAnchor = productTitle;
      firstAnchor.parentNode.insertBefore(state.builderHeaderPlaceholder, firstAnchor);
    }

    const mobile = window.matchMedia('(max-width: 767px)').matches;
    const host = ensureBuilderMobileHeaderHost();
    if (!host) return false;

    let paymentMoved = false;
    if (mobile) {
      [productTitle, rating, priceContainer, paymentInline].forEach((node) => {
        if (node && node.parentElement !== host) {
          host.appendChild(node);
          if (node === paymentInline) paymentMoved = true;
        }
      });
      const gift = utils.qs('[data-frame-builder-free-addon-offer]', state.builder);
      if (gift && (gift.parentElement !== host || host.lastElementChild !== gift)) host.appendChild(gift);
    } else {
      const placeholderParent = state.builderHeaderPlaceholder?.parentNode;
      if (placeholderParent) {
        let anchor = state.builderHeaderPlaceholder;
        [productTitle, rating, priceContainer, paymentInline].forEach((node) => {
          if (!node) return;
          if (node.parentNode !== placeholderParent || node.previousSibling !== anchor) {
            anchor.parentNode.insertBefore(node, anchor.nextSibling);
            if (node === paymentInline) paymentMoved = true;
          }
          anchor = node;
        });
      }
    }

    if (paymentMoved) refreshBuilderPaypalMessage();

    return true;
  }

  function placeZipWidget(mountId) {
    if (state.mountedId !== mountId) return false;
    if (isFramePdp() && !isBuilderOpen()) return false;

    const priceWrapper = document.querySelector('.cpb-custom-product-price-container');
    if (!priceWrapper) return false;

    let paymentInline = document.querySelector('.cpb-payment-inline');
    if (!paymentInline) {
      paymentInline = document.createElement('div');
      paymentInline.className = 'cpb-payment-inline';
      priceWrapper.insertAdjacentElement('afterend', paymentInline);
    }

    if (isFramePdp()) return true;

    let zipMount = document.querySelector('.cpb-zip-widget-mount');
    if (!zipMount) {
      zipMount = document.createElement('div');
      zipMount.className = 'cpb-zip-widget-mount';
      zipMount.style.display = 'block';
      zipMount.style.width = '100%';
      zipMount.style.marginTop = '8px';
      zipMount.style.flexBasis = '100%';
      zipMount.style.whiteSpace = 'normal';

      paymentInline.insertAdjacentElement('afterend', zipMount);
    }

    const widget = document.getElementById('shopify-zip-prod-widget');
    if (!widget) return false;
    if (widget.parentElement !== zipMount) zipMount.appendChild(widget);

    try {
      if (window.Zip?.init) window.Zip.init();
      if (window.zmWidget?.init) window.zmWidget.init();
    } catch (_) {}

    return true;
  }

  const NAV_REGEX = /PREVIOUS|NEXT|RESET|PREVIOUS TAB|NEXT TAB/i;

  function removeNavigationButtons() {
    const buttons = utils.qsa('.cpb-product-actions button, .cpb-product-actions .cpb-actions-btn');
    if (!buttons.length) return false;

    let removed = 0;
    buttons.forEach((btn) => {
      if (NAV_REGEX.test((btn.textContent || '').trim())) {
        btn.remove();
        removed++;
      }
    });

    return removed > 0;
  }

  function syncBuilderCartButtonTotal() {
    if (!isFramePdp() || !state.builder) return false;
    const price = utils.qs('.cpb-custom-product-price', state.builder) || findBestPriceEl();
    const amount = parseAmount(price?.textContent || '');
    if (amount === null || amount <= 0) return false;
    const label = 'Add to cart — ' + formatWholeAdventureMoneyCents(Math.round(amount * 100));
    utils.qsa('.cpb-add-to-cart-button', state.builder).forEach((button) => {
      const labelNode = button.querySelector('span') || button;
      if (button.disabled || button.classList.contains('gp-cpb-add-to-cart--sold-out')
        || /sold out|out of stock/i.test(labelNode.textContent || '')) return;
      button.dataset.gpIdleLabel = label;
      if (!button.classList.contains('gp-cpb-add-to-cart--busy') && labelNode.textContent !== label) {
        labelNode.textContent = label;
      }
    });
    return true;
  }

  function setBuilderCartButtonBusy(button, busy) {
    if (!button) return;

    const labelNode = button.querySelector('span') || button;
    if (!button.dataset.gpIdleLabel) {
      button.dataset.gpIdleLabel = (labelNode.textContent || button.textContent || 'Add to Cart').trim();
    }

    button.classList.toggle('gp-cpb-add-to-cart--busy', !!busy);
    button.setAttribute('aria-busy', busy ? 'true' : 'false');

    if (busy) {
      labelNode.textContent = 'Adding to cart...';
    } else {
      labelNode.textContent = button.dataset.gpIdleLabel || 'Add to Cart';
      button.removeAttribute('aria-busy');
    }
  }

  function bindBuilderCartButtonFeedback() {
    if (!state.builder) return false;

    const buttons = utils.qsa('.cpb-add-to-cart-button', state.builder);
    if (!buttons.length) return false;

    buttons.forEach((button) => {
      if (button.dataset.gpCartFeedbackBound === 'true') return;

      button.addEventListener('click', () => {
        if (button.classList.contains('gp-cpb-add-to-cart--busy') || button.disabled) return;

        setBuilderCartButtonBusy(button, true);
        utils.setTrackedTimeout(() => {
          setBuilderCartButtonBusy(button, false);
        }, 4000);
      }, true);

      button.dataset.gpCartFeedbackBound = 'true';
    });

    syncBuilderCartButtonTotal();
    state.builderCartButtonsBound = true;
    return true;
  }

  function normalizeBuilderSoldOutButtons() {
    const root = state.builder || document;
    const buttons = utils.qsa('.cpb-add-to-cart-button', root);
    if (!buttons.length) return false;

    let changed = false;

    buttons.forEach((button) => {
      const labelNode = button.querySelector('span') || button;
      const label = utils.normalizeText(labelNode.textContent || '');
      const isSoldOut = /out of stock|sold out/i.test(label);
      if (!isSoldOut) return;

      labelNode.textContent = 'SOLD OUT';
      button.classList.add('gp-cpb-add-to-cart--sold-out');
      button.disabled = true;
      button.setAttribute('disabled', 'disabled');
      button.setAttribute('aria-disabled', 'true');
      changed = true;
    });

    return changed;
  }

  function updateOutOfStockOverlays() {
    const wrappers = utils.qsa('.out-stock-wrapper');
    if (!wrappers.length) return false;

    wrappers.forEach((wrapper) => {
      const overlay = utils.qs('.out-stock-overlay span', wrapper);
      const img = utils.qs('img', wrapper);
      if (!overlay || !img) return;

      overlay.textContent = 'OUT OF STOCK';
    });

    return true;
  }

  function deriveAddonLabel(node) {
    if (!node) return '';

    const explicitLabel = node.getAttribute('data-gp-addon-label');
    if (explicitLabel) return explicitLabel;

    const img = node.querySelector('img');
    const imgAlt = img?.getAttribute('alt');
    if (imgAlt && utils.normalizeText(imgAlt) !== 'product') return imgAlt;

    const text = (node.textContent || '')
      .replace(/\$\s?[\d,]+(?:\.\d{1,2})?/g, '')
      .replace(/save\s+\$?[\d,]+/ig, '')
      .replace(/included\s*\(free\)/ig, '')
      .replace(/\s+/g, ' ')
      .trim();

    return text;
  }

  function formatMoneyCents(amountInCents) {
    const amount = Number(amountInCents || 0);
    if (!Number.isFinite(amount)) return '$0';

    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(amount, window.theme?.moneyFormat || '${{amount_no_decimals}}');
    }

    return formatPrice(String(amount / 100));
  }

  function formatWholeAdventureMoneyCents(amountInCents) {
    return formatMoneyCents(amountInCents).replace(/([.,]00)(?=\s*$)/, '');
  }

  function extractPriceDataFromNode(node) {
    if (!node) return { currentPrice: null, compareAt: null };

    const priceSelectors = [
      '[data-rrp-choice-price]',
      '.rrp-choice-price',
      '.cpb-option-price',
      '.cpb-choice-price',
      '.price',
      '.money'
    ];
    const compareSelectors = [
      '[data-compare-price]',
      '.compare-price',
      '.cpb-option-compare-price',
      '.cpb-choice-compare-price',
      '.was-price',
      '.compare-at'
    ];

    let currentPrice = null;
    let compareAt = null;

    priceSelectors.some((selector) => {
      const target = node.querySelector(selector);
      if (!target) return false;
      const amount = parseAmount(target.textContent || target.getAttribute('data-rrp-choice-price') || '');
      if (amount === null) return false;
      currentPrice = Math.round(amount * 100);
      return true;
    });

    compareSelectors.some((selector) => {
      const target = node.querySelector(selector);
      if (!target) return false;
      const amount = parseAmount(target.textContent || target.getAttribute('data-compare-price') || '');
      if (amount === null) return false;
      compareAt = Math.round(amount * 100);
      return true;
    });

    return { currentPrice, compareAt };
  }

  function getFallbackAccessoryRecord(normalizedQuery) {
    if (!normalizedQuery) return null;
    if (normalizedQuery === 'dip bars' || normalizedQuery.includes('dip bars')) {
      queueAccessoryRecordFetch('dip-bars-1');
    }
    return null;
  }

  function findAccessoryRecord(query) {
    const normalizedQuery = utils.normalizeText(query);
    if (!normalizedQuery) return null;

    const candidates = [normalizedQuery];
    const addCandidate = (value) => {
      const normalizedValue = utils.normalizeText(value);
      if (normalizedValue && !candidates.includes(normalizedValue)) {
        candidates.push(normalizedValue);
      }
    };

    if (/\b2\s*4m\b/.test(normalizedQuery) || normalizedQuery.includes('extension')) {
      addCandidate('extension monkey bars');
      addCandidate('2 4m extension kit');
      addCandidate('2.4m extension kit');
    }

    if (normalizedQuery.includes('step ups') || normalizedQuery.includes('step up')) {
      addCandidate('adjustable steps');
      addCandidate('adjustable step');
    }

    if (normalizedQuery.includes('height adjustable') || normalizedQuery.includes('levelling')) {
      addCandidate('height adjustable legs');
      addCandidate('levelling kit');
    }

    const exactMatch = accessoryIndex.find((record) => {
      const title = utils.normalizeText(record.title);
      const handle = utils.normalizeText(record.handle);
      return candidates.some((candidate) => title === candidate || handle === candidate);
    });
    if (exactMatch) return exactMatch;

    const looseMatches = accessoryIndex.filter((record) => {
      const title = utils.normalizeText(record.title);
      const handle = utils.normalizeText(record.handle);

      return candidates.some((candidate) => (
        candidate.includes(title) ||
        title.includes(candidate) ||
        candidate.includes(handle) ||
        handle.includes(candidate)
      ));
    });

    if (looseMatches.length) {
      return looseMatches.sort((a, b) => utils.normalizeText(b.title).length - utils.normalizeText(a.title).length)[0];
    }

    return getFallbackAccessoryRecord(normalizedQuery);
  }

  function isAddonNodeSelected(node) {
    if (!node) return false;

    return (
      node.classList.contains('cpb-active') ||
      node.getAttribute('aria-pressed') === 'true' ||
      node.getAttribute('aria-selected') === 'true' ||
      node.getAttribute('data-selected') === 'true' ||
      node.classList.contains('selected')
    );
  }

  function getAddonOptionNodes() {
    if (!state.builder) return [];
    return Array.from(utils.qsa('.cpb-option, .cpb-option-type-img, .cpb-category-options-container > *', state.builder));
  }

  function clearLastInteractedAddonMarker() {
    getAddonOptionNodes().forEach((option) => {
      option.removeAttribute('data-gp-last-addon');
    });
  }

  function markLastInteractedAddon(node) {
    if (!node) return;
    clearLastInteractedAddonMarker();
    node.setAttribute('data-gp-last-addon', 'true');
  }

  function getLastInteractedAddonNode() {
    if (!state.builder) return null;
    return utils.qs('[data-gp-last-addon="true"]', state.builder);
  }

  function findSelectedAddonNodeByLabel(label) {
    if (!state.builder || !label) return null;

    const normalizedLabel = utils.normalizeText(label);
    if (!normalizedLabel) return null;

    const options = utils.qsa('.cpb-option, .cpb-option-type-img, .cpb-category-options-container > *', state.builder);
    for (const option of options) {
      if (!isAddonNodeSelected(option)) continue;
      if (utils.normalizeText(deriveAddonLabel(option)) === normalizedLabel) {
        return option;
      }
    }

    return null;
  }

  function getSelectedAddonEntries() {
    return getAddonOptionNodes()
      .filter((option) => isAddonNodeSelected(option))
      .map((option) => ({
        node: option,
        label: deriveAddonLabel(option),
        isFree: !!option.closest('.promo-free-addon, .free-add-on-pdp')
      }))
      .filter((entry) => entry.label);
  }

  function syncLastSelectedAddonFromDom() {
    const entries = getSelectedAddonEntries();
    const currentLabels = entries.map((entry) => entry.label);
    const previousLabels = Array.isArray(state.selectedAddonLabels) ? state.selectedAddonLabels : [];
    const addedEntries = entries.filter((entry) => !previousLabels.includes(entry.label));
    const lastSelectedStillActive = state.lastSelectedAddonKey && currentLabels.includes(state.lastSelectedAddonKey);

    if (addedEntries.length) {
      const nextEntry = [...addedEntries].reverse().find((entry) => !entry.isFree) || addedEntries[addedEntries.length - 1];
      state.lastSelectedAddonKey = nextEntry.label;
      state.lastSelectedAddonNode = nextEntry.node;
      markLastInteractedAddon(nextEntry.node);
    } else if (lastSelectedStillActive) {
      const currentEntry = entries.find((entry) => entry.label === state.lastSelectedAddonKey);
      if (currentEntry) {
        state.lastSelectedAddonNode = currentEntry.node;
        markLastInteractedAddon(currentEntry.node);
      }
    } else if (
      state.lastSelectedAddonKey &&
      previousLabels.includes(state.lastSelectedAddonKey) &&
      !currentLabels.includes(state.lastSelectedAddonKey)
    ) {
      state.lastSelectedAddonKey = '';
      state.lastSelectedAddonNode = null;
      clearLastInteractedAddonMarker();
    } else if (!state.lastSelectedAddonKey && previousLabels.length === 0 && entries.length === 1) {
      state.lastSelectedAddonKey = entries[0].label;
      state.lastSelectedAddonNode = entries[0].node;
      markLastInteractedAddon(entries[0].node);
    }

    state.selectedAddonLabels = currentLabels;
  }

  function resolveSelectedAddonNode() {
    syncLastSelectedAddonFromDom();

    let node = getLastInteractedAddonNode();
    if (node && node.isConnected && isAddonNodeSelected(node)) {
      return node;
    }

    node = findSelectedAddonNodeByLabel(state.lastSelectedAddonKey) || state.lastSelectedAddonNode;
    if (node && node.isConnected && isAddonNodeSelected(node)) {
      markLastInteractedAddon(node);
      return node;
    }

    return null;
  }

  function decorateAddonOptionTiles() {
    if (!isFramePdp() || !state.builder) return false;

    decorateChooseAdventureCategories();
    const options = Array.from(utils.qsa('.cpb-option-type-img', state.builder));
    if (!options.length) return false;

    options.forEach((option) => {
      const img = utils.qs('img', option);
      if (!img) return;

      const isAdventureOption = !!option.closest('.cpb-choose-adventure');
      const adventureItem = isAdventureOption ? findChooseAdventureItem(option) : null;
      if (isAdventureOption && !adventureItem) return;

      option.classList.add('gp-frame-addon-tile');

      // Keep the image inside CPB's original selection control and React tree.
      const imageWrap = img.parentElement;
      if (!imageWrap.classList.contains('gp-frame-addon-tile__image')) {
        imageWrap.classList.add('gp-frame-addon-tile__image');
      }
      const selectionTarget = img.closest('[data-option-id]');
      if (selectionTarget && selectionTarget !== option) {
        selectionTarget.setAttribute('data-gp-addon-selection-target', '');
      }

      const rawLabel = (adventureItem?.title || option.getAttribute('data-gp-addon-label') || deriveAddonLabel(option) || '').trim();
      const label = isAdventureOption ? formatAdventureDisplayTitle(rawLabel) : rawLabel;
      if (!label) return;

      option.setAttribute('data-gp-addon-label', label);
      [
        'title',
        'data-original-title',
        'data-tooltip',
        'data-tippy-content',
        'aria-describedby'
      ].forEach((attr) => option.removeAttribute(attr));

      utils.qsa('[title], [data-original-title], [data-tooltip], [data-tippy-content], [aria-describedby]', option)
        .forEach((node) => {
          ['title', 'data-original-title', 'data-tooltip', 'data-tippy-content', 'aria-describedby']
            .forEach((attr) => node.removeAttribute(attr));
        });

      utils.qsa('[role="tooltip"], .tooltip, .cpb-option-tooltip, .cpb-option-tooltip-inner, [class*="tooltip-wrapper-module__tooltipLayer"]', option)
        .forEach((node) => node.remove());

      const optionId = option.getAttribute('data-option-id') || option.querySelector('[data-option-id]')?.getAttribute('data-option-id') || '';
      const optionData = getCpbOptionData(optionId);
      const record = findAccessoryRecordBySku(optionData?.sku) || findAccessoryRecord(label) || findAccessoryRecord(img.getAttribute('alt') || '');
      const variantRecord = getAccessoryVariantRecord(record, optionData?.sku);
      const pricing = extractPriceDataFromNode(option);
      const nodeText = (option.textContent || '').toLowerCase();
      const isFreeAddon = !!option.closest('.promo-free-addon, .free-add-on-pdp, .cpb-choose-adventure')
        || /included\s*\(?free\)?/i.test(nodeText)
        || /\$\s*0(?:\.00)?\b/.test(nodeText)
        || /\bfree\b/.test(nodeText);

      const currentPrice = Number(pricing.currentPrice ?? variantRecord?.price ?? record?.price ?? 0);
      const compareAt = isAdventureOption
        ? Number(adventureItem?.compare_at_cents || currentPrice || 0)
        : Number(pricing.compareAt ?? variantRecord?.compare_at_price ?? record?.compare_at_price ?? 0);
      const priceText = isFreeAddon ? 'FREE' : (currentPrice > 0 ? formatMoneyCents(currentPrice) : '');
      const compareAtText = !isFreeAddon && compareAt > currentPrice ? formatMoneyCents(compareAt) : '';

      let meta = utils.qs('.gp-frame-addon-tile__meta', option);
      if (!meta) {
        meta = document.createElement('span');
        meta.className = 'gp-frame-addon-tile__meta';
        option.appendChild(meta);
      }

      const legacyLabel = utils.qs('.cpb-option-label', option);
      if (legacyLabel && legacyLabel.parentElement && legacyLabel.parentElement !== meta && legacyLabel.parentElement !== imageWrap) {
        legacyLabel.parentElement.classList.add('gp-frame-addon-tile__legacy-meta');
      }

      let titleNode = utils.qs('.gp-frame-addon-tile__title', meta);
      if (!titleNode) {
        titleNode = document.createElement('span');
        titleNode.className = 'gp-frame-addon-tile__title';
        meta.appendChild(titleNode);
      }

      let priceRow = utils.qs('.gp-frame-addon-tile__price-row', meta);
      if (!priceRow) {
        priceRow = document.createElement('span');
        priceRow.className = 'gp-frame-addon-tile__price-row';
        meta.appendChild(priceRow);
      }

      let priceNode = utils.qs('.gp-frame-addon-tile__price', priceRow);
      if (!priceNode) {
        priceNode = document.createElement('span');
        priceNode.className = 'gp-frame-addon-tile__price';
        priceRow.appendChild(priceNode);
      }

      let compareNode = utils.qs('.gp-frame-addon-tile__compare', priceRow);
      if (!compareNode) {
        compareNode = document.createElement('span');
        compareNode.className = 'gp-frame-addon-tile__compare';
        priceRow.appendChild(compareNode);
      }

      let freeBadge = utils.qs('.gp-frame-addon-tile__free-badge', priceRow);
      if (isAdventureOption) {
        if (!freeBadge) {
          freeBadge = document.createElement('span');
          freeBadge.className = 'gp-frame-addon-tile__free-badge';
          priceRow.appendChild(freeBadge);
        }
        freeBadge.textContent = 'Free';
        freeBadge.hidden = false;
      } else if (freeBadge) {
        freeBadge.remove();
      }

      titleNode.textContent = label;
      if (isAdventureOption) {
        priceNode.classList.remove('gp-frame-choose-adventure-tile__rrp');
        const adventurePriceText = compareAt > 0 ? `${formatWholeAdventureMoneyCents(compareAt)} value` : '';
        if (priceNode.textContent !== adventurePriceText) priceNode.textContent = adventurePriceText;
        if (priceNode.hidden !== !adventurePriceText) priceNode.hidden = !adventurePriceText;
        if (compareNode.textContent) compareNode.textContent = '';
        if (!compareNode.hidden) compareNode.hidden = true;
        if (priceRow.hidden) priceRow.hidden = false;
        if (freeBadge.textContent !== 'FREE') freeBadge.textContent = 'FREE';
        if (priceRow.firstElementChild !== freeBadge) priceRow.insertBefore(freeBadge, priceRow.firstElementChild);
        option.setAttribute('aria-label', [label, adventurePriceText, 'Free'].filter(Boolean).join(' '));
      } else {
        priceNode.classList.remove('gp-frame-choose-adventure-tile__rrp');
        if (priceNode.textContent !== priceText) priceNode.textContent = priceText;
        if (priceNode.hidden !== !priceNode.textContent) priceNode.hidden = !priceNode.textContent;
        if (compareNode.textContent !== compareAtText) compareNode.textContent = compareAtText;
        if (compareNode.hidden !== !compareAtText) compareNode.hidden = !compareAtText;
        const hidePriceRow = !priceNode.textContent && !compareAtText;
        if (priceRow.hidden !== hidePriceRow) priceRow.hidden = hidePriceRow;
        option.setAttribute('aria-label', [label, priceText, compareAtText].filter(Boolean).join(' '));
      }
    });

    const adventureCategory = utils.qs('.cpb-category.cpb-choose-adventure', state.builder);
    if (adventureCategory) syncChooseAdventureSelectionPresentation(adventureCategory);

    return true;
  }

  function scrubTooltipAttrs(root) {
    if (!root) return;

    const nodes = [];
    if (root.nodeType === 1) nodes.push(root);
    if (root.querySelectorAll) {
      nodes.push(...root.querySelectorAll('[title], [data-original-title], [data-tooltip], [data-tippy-content], [aria-describedby]'));
    }

    nodes.forEach((node) => {
      ['title', 'data-original-title', 'data-tooltip', 'data-tippy-content', 'aria-describedby']
        .forEach((attr) => node.removeAttribute?.(attr));
    });
  }

  function removeTooltipLayers(root = document) {
    if (!root?.querySelectorAll) return;

    root.querySelectorAll(
      '[role="tooltip"], .tooltip, .cpb-option-tooltip, .cpb-option-tooltip-inner, [class*="tooltip-wrapper-module__tooltipLayer"], [id^="TooltipLayer"]'
    ).forEach((node) => node.remove());
  }

  function scrubBuilderTooltips(root = document) {
    scrubTooltipAttrs(root);
    removeTooltipLayers(document);
  }

  function bindTooltipScrubber() {
    if (!isFramePdp() || state.tooltipScrubberBound) return;

    state.tooltipPointerHandler = (event) => {
      const option = event.target?.closest?.('#product-builder .cpb-option-type-img, #product-builder .cpb-option');
      if (!option) return;
      scrubBuilderTooltips(option);
    };

    state.tooltipFocusHandler = (event) => {
      const option = event.target?.closest?.('#product-builder .cpb-option-type-img, #product-builder .cpb-option');
      if (!option) return;
      scrubBuilderTooltips(option);
    };

    document.addEventListener('pointerover', state.tooltipPointerHandler, true);
    document.addEventListener('focusin', state.tooltipFocusHandler, true);

    state.tooltipObserver = new MutationObserver(() => {
      scrubBuilderTooltips(document);
    });

    state.tooltipObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['title', 'data-original-title', 'data-tooltip', 'data-tippy-content', 'aria-describedby', 'class', 'id']
    });

    state.tooltipScrubberBound = true;
    scrubBuilderTooltips(document);
  }

  function scheduleAddonTileRefresh(delay = 0) {
    if (!isFramePdp() || !state.builder) return;

    const run = () => {
      decorateAddonOptionTiles();
      decorateFreeAddonCategories();
      renderSelectedAddonPanel();
    };

    if (delay > 0) {
      if (state.addonTileRefreshTimeoutId) {
        clearTimeout(state.addonTileRefreshTimeoutId);
        state.timeouts.delete(state.addonTileRefreshTimeoutId);
      }

      state.addonTileRefreshTimeoutId = utils.setTrackedTimeout(() => {
        state.addonTileRefreshTimeoutId = null;
        run();
      }, delay);
      return;
    }

    if (state.addonTileRefreshFrame) {
      cancelAnimationFrame(state.addonTileRefreshFrame);
    }

    state.addonTileRefreshFrame = requestAnimationFrame(() => {
      state.addonTileRefreshFrame = null;
      run();
    });
  }

  function ensureSelectedAddonPanel() {
    if (!isFramePdp() || !state.panelsContainer) return null;

    let panel = utils.qs('.gp-frame-selected-addon', state.panelsContainer);
    if (panel) return panel;

    panel = document.createElement('aside');
    panel.className = 'gp-frame-selected-addon';
    panel.hidden = true;

    const anchor = utils.qs('.cpb-product-actions', state.panelsContainer) || utils.qs('.cpb-option-type-multiple', state.panelsContainer);
    if (!anchor) return null;

    anchor.insertAdjacentElement('beforebegin', panel);
    return panel;
  }

  function getFreeAddonContext() {
    if (!state.builder) return null;

    const category = utils.qs('.cpb-category.promo-free-addon, .cpb-category.free-add-on-pdp', state.builder);
    if (!category) return null;

    const option = utils.qs('.cpb-active, [aria-pressed="true"], [aria-selected="true"], .selected', category)
      || utils.qs('.cpb-option-type-img, .cpb-option, button, [role="button"]', category);
    if (!option) return { category, option: null, record: null, currentPrice: 0, compareAt: 0, savings: 0, image: '', singleChoice: true };

    const label = deriveAddonLabel(option);
    const record = findAccessoryRecord(label) || findAccessoryRecord(option.querySelector('img')?.alt || '');
    const extractedPricing = extractPriceDataFromNode(option);
    const currentPrice = Number(extractedPricing.currentPrice ?? record?.price ?? 0);
    const compareAt = Number(
      extractedPricing.compareAt
      ?? record?.compare_at_price
      ?? Number(frameConfig.free_addon_compare_at_cents || 0)
      ?? (frameConfig.free_addon_value ? Number(frameConfig.free_addon_value) * 100 : 0)
      ?? 0
    );
    const savings = currentPrice > 0 ? currentPrice : (compareAt > 0 ? compareAt : 0);
    const options = utils.qsa('.cpb-option-type-img, .cpb-option, button, [role="button"]', category);

    return {
      category,
      option,
      record,
      currentPrice,
      compareAt,
      savings,
      image: record?.image || '',
      singleChoice: options.length <= 1
    };
  }

  function renderSelectedAddonPanel() {
    const panel = ensureSelectedAddonPanel();
    if (panel) {
      panel.hidden = true;
      panel.innerHTML = '';
    }
    return false;
  }

  function syncConfiguredBuilderFreeAddonOffer() {
    if (!isFramePdp() || !state.builder || !isBuilderOpen()) return false;

    const offerData = getImmediateFreeAddonOfferData();
    const markup = buildFreeAddonOfferMarkup(offerData);
    let offer = utils.qs('[data-frame-builder-free-addon-offer]', state.builder);
    if (!markup) {
      offer?.remove();
      state.builder.removeAttribute('data-gp-configured-free-addon-offer');
      return false;
    }

    const anchor = Array.from(utils.qsa('.cpb-category', state.builder)).find((category) =>
      !category.matches('.promo-free-addon, .free-add-on-pdp, .hide-step, .cpb-choose-adventure')
      && category.getClientRects().length > 0
    );
    if (!anchor) return false;

    if (!offer) {
      offer = document.createElement('aside');
      offer.className = 'gp-frame-free-addon-offer gp-frame-builder-free-addon-offer';
      offer.setAttribute('data-frame-builder-free-addon-offer', '');
      offer.setAttribute('aria-label', 'Free add-on included with your frame');
    }
    const offerCount = Array.isArray(offerData?.items) && offerData.items.length ? offerData.items.length : 1;
    setFreeAddonOfferCountClass(offer, offerCount);
    offer.classList.toggle('gp-frame-free-addon-offer--multi', offerCount > 1);
    if (offer.innerHTML.trim() !== markup.trim()) offer.innerHTML = markup;
    const mobileHost = window.matchMedia('(max-width: 767px)').matches
      ? ensureBuilderMobileHeaderHost() : null;
    if (mobileHost) {
      if (offer.parentElement !== mobileHost || mobileHost.lastElementChild !== offer) mobileHost.appendChild(offer);
    } else if (offer.nextElementSibling !== anchor) {
      anchor.before(offer);
    }
    state.builder.setAttribute('data-gp-configured-free-addon-offer', '');
    return true;
  }

  function decorateFreeAddonCategories() {
    if (!state.builder) return false;
    const hasConfiguredOffer = syncConfiguredBuilderFreeAddonOffer();

    const categories = utils.qsa('.cpb-category.promo-free-addon, .cpb-category.free-add-on-pdp', state.builder);
    if (!categories.length) return hasConfiguredOffer;

    utils.qsa('.cpb-panel-wrapper', state.panelsContainer || state.builder).forEach((wrapper) => {
      const title = utils.qs('.cpb-panel-title', wrapper);
      const categoriesContainer = utils.qs('.cpb-panel-categories-container', wrapper);
      if (!title || !categoriesContainer) return;
      if (!/free add-on/i.test(title.textContent || '')) return;
      if (categoriesContainer.children.length > 0) return;
      wrapper.hidden = true;
      wrapper.classList.add('gp-frame-empty-free-panel');
    });

    const freeContext = getFreeAddonContext();

    categories.forEach((category) => {
      category.classList.add('gp-frame-free-addon-highlight');

      let badge = utils.qs('.cpb-free-addon-badge', category);
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'cpb-free-addon-badge';
        badge.setAttribute('aria-hidden', 'true');
        badge.textContent = 'Included with your frame';
        category.appendChild(badge);
      }

      if (!freeContext || freeContext.category !== category || !freeContext.record) return;

      if (freeContext.singleChoice) {
        category.classList.add('gp-frame-free-addon-single');
      }

      const content = utils.qs('.cpb-category-content', category) || category;
      let offer = utils.qs('.gp-frame-free-addon-offer', category);
      if (!offer) {
        offer = document.createElement('div');
        offer.className = 'gp-frame-free-addon-offer gp-frame-free-addon-offer--count-1';
        content.insertAdjacentElement('afterbegin', offer);
      }

      const compareText = freeContext.compareAt > 0
        ? `<s class="gp-frame-free-addon-offer__was">${formatMoneyCents(freeContext.compareAt)}</s><strong class="gp-frame-free-addon-offer__free">Free</strong>`
        : '';

      offer.innerHTML = `
        <div class="gp-frame-free-addon-offer__media">
          ${freeContext.image ? `<img src="${freeContext.image}" alt="${freeContext.record.title}">` : ''}
        </div>
        <div class="gp-frame-free-addon-offer__content">
          <h3 class="gp-frame-free-addon-offer__title">${includedGiftTitle(freeContext.record.title)}</h3>
          <div class="gp-frame-free-addon-offer__compare">${compareText || '<strong class="gp-frame-free-addon-offer__free">Free</strong>'}<span class="gp-frame-free-addon-offer__caption">${utils.escapeHtml(window.PXUTheme.translation.gift_added_with_frame)}</span></div>
        </div>
      `;
    });

    return true;
  }

  function maybeSelectFreeAddon() {
    if (!isFramePdp()) return false;

    const promoCategory = utils.qs('.cpb-category.promo-free-addon, .cpb-category.free-add-on-pdp', state.builder);
    if (!promoCategory) return false;

    const selected = utils.qs('.cpb-active, [aria-pressed="true"]', promoCategory);
    if (selected) {
      markLastInteractedAddon(selected);
      state.lastSelectedAddonNode = selected;
      state.lastSelectedAddonKey = deriveAddonLabel(selected);
      renderSelectedAddonPanel();
      return true;
    }

    const firstOption = utils.qs('.cpb-option-type-img, .cpb-option, button, [role="button"]', promoCategory);
    if (!firstOption) return false;

    markLastInteractedAddon(firstOption);
    state.lastSelectedAddonNode = firstOption;
    state.lastSelectedAddonKey = deriveAddonLabel(firstOption);
    firstOption.click();
    return true;
  }

  function bindAddonSelection() {
    if (!isFramePdp() || !state.builder) return;

    if (!state.addonTileActivationHandler) {
      state.addonTileActivationHandler = (event) => {
        if (!isFramePdp() || !event.target || !state.builder?.contains(event.target)) return;
        const option = event.target.closest('.gp-frame-addon-tile');
        // Unavailable options do not expose CPB's button role.
        if (!option?.matches('[role="button"]:not([aria-disabled="true"])')) return;
        const target = option.querySelector('[data-gp-addon-selection-target]');
        if (!target || target.contains(event.target)) return;
        if (event.type === 'keydown' &&
            (event.target !== option || (event.key !== 'Enter' && event.key !== ' '))) return;
        // Titles, prices and keyboard activation use the same app control as the image.
        event.preventDefault();
        event.stopImmediatePropagation();
        target.click();
      };
      document.addEventListener('click', state.addonTileActivationHandler, true);
      document.addEventListener('keydown', state.addonTileActivationHandler, true);
    }

    if (!state.addonPointerHandler) {
      state.addonPointerHandler = (event) => {
        if (!state.builder || !event.target || !state.builder.contains(event.target)) return;

        const option = event.target.closest('.cpb-option, .cpb-option-type-img, .cpb-category-options-container > *');
        if (!option) return;

        state.lastSelectedAddonNode = option;
        state.lastSelectedAddonKey = deriveAddonLabel(option);
        markLastInteractedAddon(option);
      };

      document.addEventListener('pointerdown', state.addonPointerHandler, true);
    }

    if (!state.addonClickHandler) {
      state.addonClickHandler = (event) => {
        if (!state.builder || !event.target || !state.builder.contains(event.target)) return;

        const option = event.target.closest('.cpb-option, .cpb-option-type-img, .cpb-category-options-container > *');
        if (!option) return;

        state.lastSelectedAddonNode = option;
        state.lastSelectedAddonKey = deriveAddonLabel(option);
        markLastInteractedAddon(option);

        scheduleAddonTileRefresh();
        scheduleAddonTileRefresh(90);
      };

      document.addEventListener('click', state.addonClickHandler, true);
    }

    if (!state.addonEventsBound) {
      document.body.addEventListener('CPB_ON_OPTIONS_UPDATE', () => {
        const currentPriceEl = findBestPriceEl();
        if (currentPriceEl) syncAfterpayAmount(currentPriceEl.textContent);
        normalizeBuilderDiscountUi();
        scheduleBuilderCompareAtSync();
        scheduleAddonTileRefresh();
        syncFreeAddonTeaser();
        scheduleAddonTileRefresh(90);
      });
      state.addonEventsBound = true;
    }

    // MutationObserver: watch for cpb-active class changes on any addon option
    const addonContainer = state.builder.querySelector('.cpb-category-options-container') || state.builder;
    if (addonContainer && !state.addonClassObserver) {
      state.addonClassObserver = new MutationObserver((mutations) => {
        for (const m of mutations) {
          if (
            m.type === 'childList' ||
            (m.type === 'attributes' && (m.attributeName === 'class' || m.attributeName === 'aria-pressed' || m.attributeName === 'aria-selected' || m.attributeName === 'data-selected'))
          ) {
            scheduleAddonTileRefresh();
            return;
          }
        }
      });
      state.addonClassObserver.observe(addonContainer, {
        childList: true,
        attributes: true,
        attributeFilter: ['class', 'aria-pressed', 'aria-selected', 'data-selected'],
        subtree: true
      });
    }
  }

  function syncAddonRailAffordances() {
    if (!isFramePdp() || !state.builder) return false;

    decorateChooseAdventureCategories();
    const multipleGroups = utils.qsa('.cpb-category:not(.promo-free-addon):not(.free-add-on-pdp):not(.cpb-choose-adventure)', state.builder);
    if (!multipleGroups.length) return false;

    multipleGroups.forEach((category) => {
      const titleInner = utils.qs('.cpb-category-title__inner', category);
      const rail = utils.qs('.cpb-option-type-multiple', category);
      if (!titleInner || !rail) return;

      let affordance = utils.qs('.gp-frame-addon-scroll-affordance', category);
      if (!affordance) {
        affordance = document.createElement('div');
        affordance.className = 'gp-frame-addon-scroll-affordance';
        affordance.innerHTML = `
          <div class="gp-frame-addon-scroll-affordance__hint">Swipe to see more</div>
          <div class="gp-frame-addon-scroll-affordance__track"><span class="gp-frame-addon-scroll-affordance__fill"></span></div>
        `;
        titleInner.insertAdjacentElement('afterend', affordance);
      }

      const fill = utils.qs('.gp-frame-addon-scroll-affordance__fill', affordance);
      const update = () => {
        const maxScroll = Math.max(rail.scrollWidth - rail.clientWidth, 0);
        const progress = maxScroll > 0 ? rail.scrollLeft / maxScroll : 1;
        if (fill) fill.style.transform = `scaleX(${Math.max(0.08, progress)})`;
        affordance.hidden = maxScroll <= 8;
      };

      if (rail.dataset.gpAffordanceBound !== 'true') {
        rail.addEventListener('scroll', update, { passive: true });
        rail.dataset.gpAffordanceBound = 'true';
      }
      update();
    });

    return true;
  }

  function getBuilderScrollOffset() {
    const headers = utils.qsa('#header, #mobile-header');
    const headerHeight = Math.max(0, ...Array.from(headers).map(header => {
      const rect = header.getBoundingClientRect();
      return rect.width ? Math.ceil(rect.height) : 0;
    }));
    const signup = document.querySelector('.growplay-email-signup');
    const signupHeight = signup ? Math.ceil(signup.getBoundingClientRect().height) : 0;
    return Math.max(16, headerHeight + signupHeight + 12);
  }

  function jumpViewportToHeroAnchor() {
    const anchor = state.productWrapper || state.builderShell;
    if (!anchor) return;

    const topOffset = getBuilderScrollOffset();
    const targetTop = Math.max(
      0,
      Math.round(anchor.getBoundingClientRect().top + window.scrollY - topOffset)
    );

    window.scrollTo(0, targetTop);
  }

  function scrollBuilderIntoPlace() {
    if (!state.builderShell) return;

    const topOffset = getBuilderScrollOffset();

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!state.builderShell) return;
        const targetTop = Math.max(
          0,
          Math.round(state.builderShell.getBoundingClientRect().top + window.scrollY - topOffset)
        );
        if (Math.abs(targetTop - window.scrollY) > 24) {
          window.scrollTo(0, targetTop);
        }
      });
    });
  }

  function revealBuilder(scroll = true) {
    if (!isFramePdp()) return;

    if (scroll) {
      jumpViewportToHeroAnchor();
    }

    const builderReady = window.GrowplayBuilderLoader ? window.GrowplayBuilderLoader.load() : Promise.resolve();
    setBuilderVisibility(true);
    builderReady.then(() => {
      refreshBuilderRefs();
      if (!isBuilderOpen()) return;
      releaseBuilderCollapsedState();
      observeAfterpayPlacements();
      loadFrameDiscountScript().then(() => init()).catch(() => {});
    }).catch(() => {});
    observeAfterpayPlacements();
    loadFrameDiscountScript()
      .then(() => {
        if (state.builder && !window.GrowplayBuilderLoader) init();
      })
      .catch(() => {});
    utils.retry(releaseBuilderCollapsedState, [0, 120, 300, 700, 1400, 2200]);

    if (!state.builder || !state.builderShell) {
      utils.retry(() => {
        // The shopper may have closed it again before it appeared.
        if (!isBuilderOpen()) return;
        if (refreshBuilderRefs()) {
          setBuilderVisibility(true);
          releaseBuilderCollapsedState();
        }
      }, [0, 80, 200, 450, 900, 1400, 2000]);
    }

    if (state.mountedId) {
      utils.retry(() => moveDescription(state.mountedId));
      utils.retry(() => placeElements(state.mountedId));
      utils.retry(() => placeAfterpayWidget(state.mountedId));
      utils.retry(decorateAddonOptionTiles);
      utils.retry(decorateFreeAddonCategories);
      utils.retry(renderSelectedAddonPanel);
    }

    if (scroll && state.builderShell) {
      scrollBuilderIntoPlace();
    }
  }

  function bindRevealButtons() {
    if (!isFramePdp()) return;

    utils.qsa('[data-frame-open-builder], .personalize-it--js').forEach((button) => {
      if (button.dataset.gpRevealBound === 'true') return;

      button.addEventListener('click', (event) => {
        event.preventDefault();
        state.builderReturnFocus = button;
        revealBuilder(true);
        focusBuilderEntry();
      });

      button.dataset.gpRevealBound = 'true';
    });
  }

  function initStickyBar() {
    return;
  }

  function resetDesktopStickyRail(column, rail) {
    if (!column || !rail) return;

    column.style.removeProperty('min-height');
    rail.style.removeProperty('position');
    rail.style.removeProperty('top');
    rail.style.removeProperty('right');
    rail.style.removeProperty('bottom');
    rail.style.removeProperty('left');
    rail.style.removeProperty('width');
    rail.style.removeProperty('max-height');
    rail.style.removeProperty('overflow-y');
    rail.style.removeProperty('padding-right');
  }

  function resetDesktopStickyMedia(column, media) {
    if (!column || !media) return;

    column.style.removeProperty('min-height');
    column.style.removeProperty('position');
    media.style.removeProperty('position');
    media.style.removeProperty('top');
    media.style.removeProperty('left');
    media.style.removeProperty('right');
    media.style.removeProperty('bottom');
    media.style.removeProperty('width');
    media.style.removeProperty('max-width');
    media.style.removeProperty('z-index');
  }

  function initDesktopStickyMedia() {
    if (!isFramePdp()) return;

    const section = utils.qs('.gp-frame-product-section');
    const column = utils.qs('.product__images', section || document);
    const gallery = utils.qs('.product-gallery', column || document);
    const media = utils.qs('.product-gallery__main', column || document);
    const infoColumn = utils.qs('.gp-frame-product__information', section || document);
    const hasLeftThumbs = !!gallery && gallery.classList.contains('product-gallery--left-thumbnails');
    if (!section || !column) return;

    if (gallery) resetDesktopStickyMedia(column, gallery);
    if (media) resetDesktopStickyMedia(column, media);

    if (state.desktopMediaStickyHandlersBound) {
      if (state.desktopMediaStickyScrollHandler) {
        window.removeEventListener('scroll', state.desktopMediaStickyScrollHandler);
      }
      if (state.desktopMediaStickyResizeHandler) {
        window.removeEventListener('resize', state.desktopMediaStickyResizeHandler);
      }
      state.desktopMediaStickyHandlersBound = false;
      state.desktopMediaStickyScrollHandler = null;
      state.desktopMediaStickyResizeHandler = null;
      state.desktopMediaStickyUpdate = null;
      state.desktopMediaStickyRafId = null;
    }

    return;

    if (!gallery || !infoColumn || !hasLeftThumbs) return;

    const updateStickyMedia = () => {
      if (window.innerWidth < 768 || isBuilderOpen()) {
        resetDesktopStickyMedia(column, gallery);
        return;
      }

      const galleryHeight = Math.ceil(gallery.offsetHeight);
      if (!galleryHeight) {
        resetDesktopStickyMedia(column, gallery);
        return;
      }

      const topOffset = 24;
      const sectionRect = section.getBoundingClientRect();
      const infoRect = infoColumn.getBoundingClientRect();
      const docY = window.scrollY;
      const sectionTop = Math.round(sectionRect.top + docY);
      const infoTop = Math.round(infoRect.top + docY);
      const infoBottom = Math.round(infoRect.bottom + docY);
      const stickyContainerHeight = Math.max(
        galleryHeight,
        Math.ceil(infoColumn.offsetHeight),
        infoBottom - sectionTop
      );
      const stickyStart = Math.min(sectionTop, infoTop) - topOffset;
      const stickyEnd = Math.max(stickyStart, infoBottom - galleryHeight - topOffset);
      const currentY = docY;

      column.style.position = 'relative';
      column.style.minHeight = `${stickyContainerHeight}px`;

      if (currentY <= stickyStart) {
        resetDesktopStickyMedia(column, gallery);
        column.style.position = 'relative';
        return;
      }

      if (currentY >= stickyEnd) {
        gallery.style.position = 'absolute';
        gallery.style.top = 'auto';
        gallery.style.left = '0';
        gallery.style.right = '0';
        gallery.style.bottom = '0';
        gallery.style.width = '100%';
        gallery.style.maxWidth = '100%';
        gallery.style.zIndex = '3';
        return;
      }

      const columnRect = column.getBoundingClientRect();
      gallery.style.position = 'fixed';
      gallery.style.top = `${topOffset}px`;
      gallery.style.left = `${Math.round(columnRect.left)}px`;
      gallery.style.right = 'auto';
      gallery.style.bottom = 'auto';
      gallery.style.width = `${Math.round(columnRect.width)}px`;
      gallery.style.maxWidth = `${Math.round(columnRect.width)}px`;
      gallery.style.zIndex = '20';
    };

    const requestUpdate = () => {
      if (state.desktopMediaStickyRafId) return;
      state.desktopMediaStickyRafId = requestAnimationFrame(() => {
        state.desktopMediaStickyRafId = null;
        updateStickyMedia();
      });
    };

    state.desktopMediaStickyUpdate = updateStickyMedia;
    state.desktopMediaStickyScrollHandler = requestUpdate;
    state.desktopMediaStickyResizeHandler = requestUpdate;
    window.addEventListener('scroll', state.desktopMediaStickyScrollHandler, { passive: true });
    window.addEventListener('resize', state.desktopMediaStickyResizeHandler);
    state.desktopMediaStickyHandlersBound = true;
    requestUpdate();
  }

  function initDesktopStickyRail() {
    if (!isFramePdp()) return;

    const column = utils.qs('.gp-frame-product__information');
    const rail = utils.qs('.gp-frame-product__information-inner', column || document);
    if (!column || !rail) return;

    resetDesktopStickyRail(column, rail);

    if (state.desktopStickyHandlersBound) {
      if (state.desktopStickyScrollHandler) {
        window.removeEventListener('scroll', state.desktopStickyScrollHandler);
      }
      if (state.desktopStickyResizeHandler) {
        window.removeEventListener('resize', state.desktopStickyResizeHandler);
      }
      state.desktopStickyHandlersBound = false;
      state.desktopStickyScrollHandler = null;
      state.desktopStickyResizeHandler = null;
      state.desktopStickyUpdate = null;
      state.desktopStickyRafId = null;
    }
  }

  function getKlaviyoReviewHost() {
    return document.querySelector('#fulfilled-reviews-all[data-id], #klaviyo-product-reviews-wrapper, [id*="klaviyo_reviews_product_reviews"], .reviews_all_container');
  }

  function ensureKlaviyoReviewHostVisible() {
    const host = getKlaviyoReviewHost();
    if (!host) return false;

    host.hidden = false;
    host.removeAttribute('hidden');
    host.style.removeProperty('display');
    host.style.removeProperty('visibility');
    host.style.removeProperty('opacity');

    const appBlock = host.closest('.shopify-app-block, .shopify-block, .apps__block');
    if (appBlock) {
      appBlock.hidden = false;
      appBlock.removeAttribute('hidden');
      appBlock.style.removeProperty('display');
      appBlock.style.removeProperty('visibility');
      appBlock.style.removeProperty('opacity');
    }

    const section = host.closest('.shopify-section');
    if (section) {
      section.hidden = false;
      section.removeAttribute('hidden');
      section.style.removeProperty('display');
      section.style.removeProperty('visibility');
      section.style.removeProperty('opacity');
    }

    return true;
  }

  function isBackForwardNavigation() {
    try {
      return performance.getEntriesByType('navigation')[0]?.type === 'back_forward';
    } catch (_) {
      return false;
    }
  }

  function getKlaviyoReviewsReloadKey() {
    return `gp-klaviyo-reviews-reload:${window.location.pathname}`;
  }

  let reviewsObserver;
  let reviewsObservedHost;
  let reviewsTimer;
  let reviewsFallbackRequested = false;
  let reviewsUnblocked = false;

  function hasMountedKlaviyoReviews() {
    return !!document.querySelector(
      '#fulfilled-reviews-all .kl_reviews__summary, #klaviyo-product-reviews-wrapper .kl_reviews__summary, #fulfilled-reviews-all .kl_reviews__review_list, #klaviyo-product-reviews-wrapper .kl_reviews__review_list'
    );
  }

  function stopReviewsRecovery() {
    reviewsObserver?.disconnect();
    reviewsObserver = null;
    reviewsObservedHost = null;
    clearTimeout(reviewsTimer);
    reviewsTimer = null;
  }

  function queueReviewsRecovery() {
    if (reviewsTimer || reviewsFallbackRequested) return;
    if (hasMountedKlaviyoReviews()) {
      stopReviewsRecovery();
      return;
    }
    // Give the native widget time to mount before attempting one fallback.
    reviewsTimer = setTimeout(() => {
      reviewsTimer = null;
      if (hasMountedKlaviyoReviews()) {
        stopReviewsRecovery();
        return;
      }
      const source = Array.from(document.scripts).find(script =>
        script.src.includes('static.klaviyo.com/onsite/js/') && script.src.includes('company_id=')
      );
      if (!source || document.querySelector('script[data-gp-klaviyo-reviews-reload="true"]')) return;
      reviewsFallbackRequested = true;
      stopReviewsRecovery();
      const script = document.createElement('script');
      script.async = true;
      script.src = `${source.src}${source.src.includes('?') ? '&' : '?'}gp_reviews_reload=${Date.now()}`;
      script.setAttribute('data-gp-klaviyo-reviews-reload', 'true');
      document.head.appendChild(script);
    }, CONFIG.KLAVIYO_REVIEWS_RECOVERY_DELAY_MS);
  }

  function scheduleKlaviyoReviewsRecovery() {
    const host = getKlaviyoReviewHost();
    if (!host || reviewsFallbackRequested) return;
    if (hasMountedKlaviyoReviews()) {
      stopReviewsRecovery();
      return;
    }
    ensureKlaviyoReviewHostVisible();
    if (!reviewsUnblocked && typeof window.yett?.unblock === 'function') {
      window.yett.unblock('product-reviews');
      reviewsUnblocked = true;
    }
    if (reviewsObservedHost === host) return;
    stopReviewsRecovery();
    reviewsObservedHost = host;
    reviewsObserver = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) queueReviewsRecovery();
    }, {rootMargin: '400px 0px'});
    reviewsObserver.observe(host);
  }

  window.addEventListener('pagehide', stopReviewsRecovery);

  function init() {
    // The native builder (snippets/frame-builder.liquid + assets/frame-builder.js)
    // renders its own tiles, price and cart button; the CPB decorators below
    // must not touch it. Opening/closing still goes through setBuilderVisibility.
    if (document.querySelector('#product-builder[data-gp-native-builder]')) return;

    const builderWasOpen = isBuilderOpen();

    if (isFramePdp() && !builderWasOpen) {
      refreshBuilderRefs();
      setBuilderVisibility(false);
      return;
    }

    cleanup();
    state.mountedId = utils.newMountId();
    const mountId = state.mountedId;

    if (!initGlobals()) return;

    ensureFrameHeroPurchaseUi();
    enhanceHeroPricingUi();
    syncHeroRatingHost();
    ensureHeroStars();
    keepBaseProductVisible();
    if (isFramePdp()) setBuilderVisibility(builderWasOpen);
    if (builderWasOpen && document.activeElement === state.builderShell) focusBuilderEntry();

    bindGrowplayAccordionFallback();
    normalizeBuilderDescriptionContent();

    bindRevealButtons();
    bindTooltipScrubber();
    initStickyBar();
    initDesktopStickyRail();
    initDesktopStickyMedia();
    syncFreeAddonTeaser();
    syncBackorderStatusPill();
    scheduleAfterpayShadowFix();

    if (!isFramePdp() || isBuilderOpen()) {
      ensureCustomPriceContainer();
      bindAddonSelection();
      observeAfterpayPlacements();
      decorateAddonOptionTiles();
      decorateFreeAddonCategories();
      utils.retry(() => placeElements(mountId));
      utils.retry(() => placeAfterpayWidget(mountId));
      utils.retry(() => placeZipWidget(mountId));
      utils.retry(removeNavigationButtons);
      utils.retry(normalizeBuilderSoldOutButtons);
      utils.retry(bindBuilderCartButtonFeedback);
      utils.retry(syncBackorderStatusPill);
      utils.retry(decorateAddonOptionTiles);
      utils.retry(decorateFreeAddonCategories);
      utils.retry(syncAddonRailAffordances);
      utils.retry(syncBuilderMobileHeaderLayout);
      utils.retry(() => maybeSelectFreeAddon());
      utils.retry(normalizeBuilderDescriptionContent);
      utils.retry(() => {
        syncGrowplayAccordions(state.builder);
        return true;
      });
    }

    CONFIG.OOS_RETRY_DELAYS_MS.forEach((delay) => {
      utils.setTrackedTimeout(() => updateOutOfStockOverlays(), delay);
      utils.setTrackedTimeout(() => normalizeBuilderSoldOutButtons(), delay);
    });

    const heroPriceText = findHeroPriceText();
    if (heroPriceText) syncStandardAfterpayAmount(heroPriceText);

    scheduleKlaviyoReviewsRecovery();
  }

  document.body.addEventListener('CPB_ON_PRODUCT_MOUNTED', init);
  document.addEventListener('DOMContentLoaded', () => {
    bindGrowplayAccordionFallback();
    ensureFrameHeroPurchaseUi();
    enhanceHeroPricingUi();
    keepBaseProductVisible();
    bindRevealButtons();
    initStickyBar();
    initDesktopStickyRail();
    initDesktopStickyMedia();
    syncFreeAddonTeaser();
    syncBackorderStatusPill();
    scheduleKlaviyoReviewsRecovery();
  });
  window.addEventListener('resize', () => {
    syncBuilderMobileHeaderLayout();
    syncConfiguredBuilderFreeAddonOffer();
    syncBuilderCartButtonTotal();
    syncAddonRailAffordances();
  });
  window.addEventListener('load', () => {
    scheduleKlaviyoReviewsRecovery();
  });
  window.addEventListener('pageshow', (event) => {
    scheduleKlaviyoReviewsRecovery({
      fromBackForwardRestore: !!event.persisted || isBackForwardNavigation()
    });
  });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible' || !isBackForwardNavigation()) return;

    scheduleKlaviyoReviewsRecovery({
      fromBackForwardRestore: true
    });
  });
  document.addEventListener('shopify:section:load', () => {
    scheduleKlaviyoReviewsRecovery();
  });
  window.addEventListener('pagehide', cleanup);
  window.addEventListener('beforeunload', cleanup);
})();
